// ***START: ComponentAccordion***
/*
- use `data-trigger` & `data-target` attribute
- use `data-close-sibling` attribute, if you wat close all sibling when - open the accordion
- use `c-accordion__target` & `is-active` class name

- use case:
<c-accordion data-close-sibling="true">
	{% for block in blocks %}
		<button data-trigger="{{ block.id }}">
			{{ block.settings.title }}
		</button>
		<div class="c-accordion__target" data-target="{{ block.id }}" aria-hidden="true">
			<div class="c-accordion__content">{{ block.settings.content }}</div>
		</div>
	{% endfor %}
</c-accordion>
*/
class ComponentAccordion extends HTMLElement {
	constructor() {
		super();
		this.triggers = this.querySelectorAll("[data-trigger]");
		this.targets = this.querySelectorAll("[data-target]");
		this.closeSibling = this.dataset.closeSibling === "true" || false;
		this.toggleAccordion = this.toggleAccordion.bind(this);
	}

	connectedCallback() {
		this.triggers.forEach((trigger) => {
			trigger.addEventListener("click", this.toggleAccordion);
		});
	}

	disconnectedCallback() {
		this.triggers.forEach((trigger) => {
			trigger.removeEventListener("click", this.toggleAccordion);
		});
	}

	toggleAccordion(e) {
		const triggerEl = e.target.closest("[data-trigger]");
		const currentTargetId = triggerEl.dataset.trigger;
		const targetEls = this.querySelectorAll(
			`[data-target="${currentTargetId}"]`
		);

		if (!triggerEl || !targetEls) return;

		// toggle current accordion
		if (triggerEl.classList.contains("is-active")) {
			triggerEl.classList.remove("is-active");
			targetEls.forEach((el) => {
				el.classList.remove("is-active");
				el.setAttribute("aria-hidden", true);
			});
			return;
		}
		if (this.closeSibling) {
			this.triggers.forEach((el) => {
				el.classList.remove("is-active");
			});
			this.targets.forEach((el) => {
				el.classList.remove("is-active");
				el.setAttribute("aria-hidden", true);
			});
		}

		triggerEl.classList.add("is-active");
		targetEls.forEach((el) => {
			el.classList.add("is-active");
			el.setAttribute("aria-hidden", false);
		});
	}
}
customElements.define("c-accordion", ComponentAccordion);
// ***END: ComponentAccordion***

// ***START: ComponentMarquee***
class ComponentMarquee extends HTMLElement {
	constructor() {
		super();
		this.contentBlocks = Array.from(
			this.querySelectorAll(".js-marquee-content")
		);
		this.blockCount = this.contentBlocks[0]?.childNodes.length || 0;
		this.pixelPerSecond = 50;
		this.observer = null;
	}

	connectedCallback() {
		if (this.contentBlocks.length === 0) return;

		this.setupMarquee();
		this.setupResizeObserver();
	}

	disconnectedCallback() {
		this.observer?.disconnect();
	}

	setupMarquee() {
		const { clientWidth: blockWidth } = this.contentBlocks[0];
		const factor = Math.ceil(window.innerWidth / blockWidth);
		const speedFactor = parseFloat(this.dataset.speedFactor) || 1;
		const speed = (blockWidth * factor) / this.pixelPerSecond / speedFactor;

		this.contentBlocks.forEach((block) => {
			block.style.animationDuration = `${speed}s`;
			this.fillBlock(block, factor);
		});
	}

	fillBlock(block, factor) {
		if (!Number.isFinite(factor)) return;

		const fragment = document.createDocumentFragment();
		for (let i = 0; i < factor - 1; i++) {
			fragment.append(
				...Array.from(block.childNodes).map((node) => node.cloneNode(true))
			);
		}
		block.append(fragment);
	}

	setupResizeObserver() {
		this.observer = new ResizeObserver(this.handleResize.bind(this));
		this.observer.observe(this);
	}

	handleResize() {
		this.contentBlocks.forEach((block) => {
			block.style.animationDuration = "";
			while (block.childElementCount > this.blockCount) {
				block.lastElementChild.remove();
			}
		});
		this.setupMarquee();
	}
}
customElements.define("c-marquee", ComponentMarquee);
// ***END: ComponentMarquee***

// ***START: ComponentSlider***
class ComponentSlider extends HTMLElement {
	constructor() {
		super();
	}

	connectedCallback() {
		this.init();
		this.initializeNavigation();
	}

	init = () => {
		this.viewportNode = this.querySelector(".js-embla-viewport");
		this.containerNode = this.querySelector(".js-embla-container");
		this.prevButtonNode = this.querySelector(".js-embla-button-prev");
		this.nextButtonNode = this.querySelector(".js-embla-button-next");
		this.dotsNode = this.querySelector(".js-embla-dots");
		this.thumbNode = this.querySelector(".js-embla-thumbs");
		this.isVertical = this.dataset.emblaVertical === "true";

		// Plugins
		this.enableFade = this.dataset.emblaFade === "true";
		this.enableAutoHeight = this.dataset.emblaAutoHeight === "true";
		this.enableAutoplay = this.dataset.emblaAutoplay === "true";
		const autoplayDelay = this.hasAttribute("data-embla-delay")
			? parseInt(this.dataset.emblaDelay)
			: 4000;
		const autoplay = EmblaCarouselAutoplay({
			playOnInit: this.enableAutoplay,
			delay: autoplayDelay,
			stopOnInteraction: false,
			stopOnMouseEnter: true,
		});
		this.enableAutoScroll = this.dataset.emblaAutoScroll === "true";
		const autoScrollSpeed = this.hasAttribute("data-embla-scroll-speed")
			? parseInt(this.dataset.emblaScrollSpeed)
			: 2;
		const autoScrollDelay = this.hasAttribute("data-embla-scroll-delay")
			? parseInt(this.dataset.emblaScrollDelay)
			: 0;
		const autoScrollDirection =
			this.dataset.emblaScrollBackward === "true" ? "backward" : "forward";
		const autoScroll = EmblaCarouselAutoScroll({
			playOnInit: this.enableAutoScroll,
			speed: autoScrollSpeed,
			startDelay: autoScrollDelay,
			direction: autoScrollDirection,
			stopOnInteraction: false,
			stopOnMouseEnter: true,
		});

		const plugins = [
			EmblaCarouselClassNames(),
			...(vs.isTouchDevice ? [EmblaCarouselWheelGestures()] : []),
			...(this.enableAutoplay ? [autoplay] : []),
			...(this.enableAutoScroll ? [autoScroll] : []),
			...(this.enableFade ? [EmblaCarouselFade()] : []),
			...(this.enableAutoHeight ? [EmblaCarouselAutoHeight()] : []),
		];

		this.isActive =
			this.containerNode.scrollWidth > this.containerNode.offsetWidth;

		if (!this.isActive) {
			this.classList.add("is-inactive");
		}

		const baseOptions = JSON.parse(
			this.dataset.emblaSlider.replace(/'/g, '"').trim()
		);

		const options = {
			axis: this.isVertical ? "y" : "x",
			watchDrag: this.isActive,
			...baseOptions,
			...(vs.isTabletDown ? { align: "center" } : {}),
			...(vs.isTabletDown ? { loop: "true" } : {}),
		};

		// Init Embla
		this.embla = EmblaCarousel(this.viewportNode, options, plugins);
		// destroy embla if contains only one item
		this.slideNodes = this.embla.slideNodes();

		if (this.slideNodes.length === 1) {
			this.classList.add("is-inactive");
			this.prevButtonNode?.setAttribute("disabled", "disabled");
			this.nextButtonNode?.setAttribute("disabled", "disabled");
			this.embla.destroy();
		}

		// Clone nodes when autoScroll is enabled but slides are insufficient to loop
		this.initLoopClones();

		this.embla.on("resize", this.toggleActiveState.bind(this));
	};

	initLoopClones = () => {
		if (!(this.isActive && this.enableAutoScroll)) return;

		// Remove old clones
		this.containerNode
			.querySelectorAll(".is-clone")
			.forEach((clone) => clone.remove());

		// Duplicate each slide
		this.slideNodes.forEach((slide) => {
			const clone = slide.cloneNode(true);
			clone.classList.remove("is-snapped", "is-in-view");
			clone.classList.add("is-clone");
			this.containerNode.appendChild(clone);
		});

		// Re-init Embla so it recalculates with new slides
		this.embla.reInit();
	};

	onSelectSlide(callback) {
		if (!this.embla) return;
		this.embla.on("select", () => {
			const selectedIndex = this.embla.selectedScrollSnap();
			callback(selectedIndex);
		});
	}

	// Prev & Next Buttons
	addTogglePrevNextBtnsActive = (emblaApi, prevBtn, nextBtn) => {
		const togglePrevNextBtnsState = () => {
			if (emblaApi.canScrollPrev()) prevBtn.removeAttribute("disabled");
			else prevBtn.setAttribute("disabled", "disabled");

			if (emblaApi.canScrollNext()) nextBtn.removeAttribute("disabled");
			else nextBtn.setAttribute("disabled", "disabled");
		};

		emblaApi
			.on("select", togglePrevNextBtnsState)
			.on("init", togglePrevNextBtnsState)
			.on("reInit", togglePrevNextBtnsState);

		return () => {
			prevBtn.removeAttribute("disabled");
			nextBtn.removeAttribute("disabled");
		};
	};
	addPrevNextBtnsClickHandlers = (emblaApi, prevBtn, nextBtn) => {
		const scrollPrev = () => {
			emblaApi.scrollPrev();
		};
		const scrollNext = () => {
			emblaApi.scrollNext();
		};
		prevBtn.addEventListener("click", scrollPrev, false);
		nextBtn.addEventListener("click", scrollNext, false);

		const removeTogglePrevNextBtnsActive = this.addTogglePrevNextBtnsActive(
			emblaApi,
			prevBtn,
			nextBtn
		);

		return () => {
			removeTogglePrevNextBtnsActive();
			prevBtn.removeEventListener("click", scrollPrev, false);
			nextBtn.removeEventListener("click", scrollNext, false);
		};
	};

	// Dots
	addDotBtnsAndClickHandlers = (emblaApi, dotsNode) => {
		let dotNodes = [];

		const addDotBtnsWithClickHandlers = () => {
			dotsNode.innerHTML = emblaApi
				.scrollSnapList()
				.map(
					() =>
						'<button class="embla__dot c-slider__dot" type="button" aria-label="Go to slide"><svg class="svg-slider-dot" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10.8 10.8"><path fill="transparent" d="M9.4 9c-.4.5-1 .8-1.6.9-.6.1-1.3.2-2.1.2-.7 0-1.4 0-2.1-.2-.7-.1-1.2-.3-1.7-.6-.5-.4-.8-.9-.9-1.6-.1-.6-.2-1.3-.2-2s0-1.4.2-2.1c0-.6.3-1.2.5-1.6.3-.5.9-.8 1.5-.9C3.6.9 4.3.8 5.1.8h2.1c.6 0 1.2.3 1.6.5.5.4.8 1 .9 1.6.1.5.2 1 .2 1.6v.6c0 .8 0 1.5-.1 2.1-.1.7-.3 1.3-.6 1.7Z" style="stroke:currentColor;stroke-width:1.5px"/></svg></button>'
				)
				.join("");

			const scrollTo = (index) => {
				emblaApi.scrollTo(index);
			};

			dotNodes = Array.from(dotsNode.querySelectorAll(".embla__dot"));
			dotNodes.forEach((dotNode, index) => {
				dotNode.addEventListener("click", () => scrollTo(index), false);
			});
		};

		const toggleDotBtnsActive = () => {
			const previous = emblaApi.previousScrollSnap();
			const selected = emblaApi.selectedScrollSnap();
			dotNodes[previous].classList.remove("is-selected");
			dotNodes[selected].classList.add("is-selected");
		};

		emblaApi
			.on("init", addDotBtnsWithClickHandlers)
			.on("reInit", addDotBtnsWithClickHandlers)
			.on("init", toggleDotBtnsActive)
			.on("reInit", toggleDotBtnsActive)
			.on("select", toggleDotBtnsActive);

		return () => {
			dotsNode.innerHTML = "";
		};
	};

	// Thumbnail navigation
	initializeThumbnailsNavigation() {
		this.viewportThumbNode = this.thumbNode.querySelector(
			".js-embla-thumbs-viewport"
		);
		this.emblaThumb = EmblaCarousel(this.viewportThumbNode, {
			axis: "y",
			skipSnaps: true,
		});
		this.addThumbBtnsClickHandlers();
		this.toggleThumbBtnsActive();
	}

	addThumbBtnsClickHandlers() {
		const slidesThumbs = this.emblaThumb.slideNodes();
		const scrollToIndex = slidesThumbs.map(
			(_, index) => () => this.embla.scrollTo(index)
		);
		slidesThumbs.forEach((slideNode, index) => {
			slideNode.addEventListener("click", scrollToIndex[index], false);
		});

		this.removeThumbBtnsClickHandlers = () => {
			slidesThumbs.forEach((slideNode, index) => {
				slideNode.removeEventListener("click", scrollToIndex[index], false);
			});
		};
	}

	toggleThumbBtnsActive() {
		const slidesThumbs = this.emblaThumb.slideNodes();
		const toggleThumbBtnsState = () => {
			this.emblaThumb.scrollTo(this.embla.selectedScrollSnap());
			const previous = this.embla.previousScrollSnap();
			const selected = this.embla.selectedScrollSnap();
			slidesThumbs[previous].classList.remove("is-selected");
			slidesThumbs[selected].classList.add("is-selected");
		};

		this.embla.on("select", toggleThumbBtnsState);
		this.emblaThumb.on("init", toggleThumbBtnsState);

		this.removeToggleThumbBtnsActive = () => {
			const selected = this.embla.selectedScrollSnap();
			slidesThumbs[selected].classList.remove("is-selected");
		};
	}

	initializeNavigation = () => {
		if (this.slideNodes.length === 1) return;
		if (this.prevButtonNode && this.nextButtonNode) {
			const removePrevNextBtnsClickHandlers = this.addPrevNextBtnsClickHandlers(
				this.embla,
				this.prevButtonNode,
				this.nextButtonNode
			);
			this.embla.on("destroy", removePrevNextBtnsClickHandlers);
		}
		if (this.dotsNode) {
			const removeDotBtnsAndClickHandlers = this.addDotBtnsAndClickHandlers(
				this.embla,
				this.dotsNode
			);
			this.embla.on("destroy", removeDotBtnsAndClickHandlers);
		}
		if (this.thumbNode) {
			this.initializeThumbnailsNavigation();
		}
	};

	toggleActiveState() {
		// disable dragging when all items are in view
		if (
			this.isActive &&
			this.containerNode.scrollWidth <= this.containerNode.offsetWidth
		) {
			this.embla.reInit({ watchDrag: false });
			this.classList.add("is-inactive");
			this.isActive = false;
		}
		// re-enable dragging when items leave view
		else if (
			!this.isActive &&
			this.containerNode.scrollWidth > this.containerNode.offsetWidth
		) {
			this.embla.reInit({ watchDrag: true });
			this.classList.remove("is-inactive");
			this.isActive = true;
		}

		// Clone nodes when autoScroll is enabled but slides are insufficient to loop
		this.initLoopClones();
	}
}
customElements.define("c-slider", ComponentSlider);

// ***END: ComponentSlider***

// ***START: ComponentScrollStory***
class ComponentScrollStory extends HTMLElement {
	constructor() {
		super();
		this.style.display = "block"; // ensure the component is block level
		this.updateTextSticky = this.updateTextSticky.bind(this);
		this.updateImageSticky = this.updateImageSticky.bind(this);

		this.handleImageIntersection = this.handleImageIntersection.bind(this);
		this.imageObservers = [];

		// GSAP
		this.initScrollAnimations = this.initScrollAnimations.bind(this);
		this.scrollTriggers = [];
		this.timelines = [];
	}

	connectedCallback() {
		// Find blocks inside this component
		const firstBlock = this.querySelector(".js-scroll-story-block--first");
		const secondBlock = this.querySelector(".js-scroll-story-block--second");

		if (!firstBlock || !secondBlock) return;

		this.firstBlock = firstBlock;
		this.secondBlock = secondBlock;

		this.updateTextSticky();

		this.images = Array.from(this.querySelectorAll(".js-scroll-story-image"));
		this.updateImageSticky();

		window.addEventListener("resize", this.updateTextSticky);
		window.addEventListener("resize", this.updateImageSticky);

		if (document.fonts && document.fonts.ready) {
			document.fonts.ready.then(this.updateTextSticky);
			document.fonts.ready.then(this.updateImageSticky);
		}

		this.handleImageIntersection();

		// GSAP scroll animations
		this.initScrollAnimations();
	}

	disconnectedCallback() {
		window.removeEventListener("resize", this.updateTextSticky);
		window.removeEventListener("resize", this.updateImageSticky);
		this.imageObservers.forEach((observer) => observer.disconnect());

		// Kill GSAP instances
		this.timelines.forEach((tl) => tl?.kill && tl.kill());
		this.scrollTriggers.forEach((st) => st?.kill && st.kill());
		this.timelines = [];
		this.scrollTriggers = [];
	}
	updateTextSticky() {
		const { firstBlock, secondBlock } = this;
		if (!firstBlock || !secondBlock) return;

		const firstBlockStyles = window.getComputedStyle(firstBlock);
		const firstBlockTop = parseInt(firstBlockStyles.top) || 0;
		const secondBlockHeight = secondBlock.clientHeight || 0;
		const svg = secondBlock.querySelector("svg");

		let svgTranslateY = 0;
		if (svg) {
			const svgStyles = window.getComputedStyle(svg);
			const svgTransform = svgStyles.transform || "";
			const matchTranslateY = svgTransform.match(/matrix.*\((.+)\)/);

			if (matchTranslateY) {
				const values = matchTranslateY[1].split(",").map(parseFloat);
				svgTranslateY = Math.round(Math.abs(values[5])) || 0; // ty is the 6th value
			}
		}

		const secondBlockOffset =
			firstBlock.clientHeight + firstBlockTop + svgTranslateY;
		firstBlock.style.marginBottom = `calc(${
			secondBlockHeight + svgTranslateY
		}px + var(--text-gap))`;
		secondBlock.style.top = `calc(${secondBlockOffset}px + var(--text-gap))`;

		const parent = this.firstBlock.parentElement;
		const stickyTextTotalHeight = secondBlockOffset + secondBlockHeight;

		if (parent) {
			const paddingBottom = `calc(${
				window.innerHeight - stickyTextTotalHeight
			}px - var(--text-gap))`;
			parent.style.setProperty("--container-padding-bottom", paddingBottom);
			parent.style.paddingBottom = paddingBottom;
		}
	}
	updateImageSticky() {
		this.images?.forEach((img) => {
			const halfHeight = img.offsetHeight / 2;
			img.style.top = `calc(50% - ${halfHeight}px)`;
		});
		const imageWrapper = this.querySelector(".js-scroll-story-image-wrapper");
		if (imageWrapper) {
			const imageHeight = this.images?.[0].offsetHeight || 0;
			imageWrapper.style.marginBottom = `calc(${
				window.innerHeight / 2 - imageHeight / 2
			}px - var(--container-padding-bottom))`;
		}
	}

	handleImageIntersection() {
		// Intersection Observer for rotation
		this.images?.forEach((img) => {
			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							img.classList.add("is-in-view");
						} else {
							img.classList.remove("is-in-view");
						}
					});
				},
				{
					threshold: 0,
					rootMargin: "0px 0px -45% 0px",
				}
			);
			observer.observe(img);
			this.imageObservers.push(observer);
		});
	}

	initScrollAnimations() {
		if (!window.gsap || !window.ScrollTrigger) return;

		const first = this.querySelector(".js-scroll-story-block--first");
		const label = this.querySelector(".js-scroll-story-label");
		const main = this.querySelector(".js-scroll-story-main");

		const second = this.querySelector(".js-scroll-story-block--second");
		const footer = this.querySelector(".js-scroll-story-footer");
		const svgPath = second?.querySelector("svg path");

		// First block: 1) line draw, 2) label in, 3) paragraph in
		if (first && label && main) {
			gsap.set(label, { "--deco-progress": 0 });

			const tl1 = gsap.timeline({
				scrollTrigger: {
					trigger: first,
					start: "top 60%",
					once: true,
				},
			});

			tl1.from(label, {
				opacity: 0,
				y: 12,
				duration: 0.45,
				ease: "power2.out",
			});
			tl1.to(
				label,
				{
					duration: 0.6,
					ease: "power2.out",
					"--deco-progress": 1,
				},
				"=-0.3"
			);
			tl1.from(
				main,
				{
					opacity: 0,
					yPercent: 3,
					duration: 0.55,
					ease: "power2.out",
				},
				"-=0.3"
			);

			this.timelines.push(tl1);
			this.scrollTriggers.push(tl1.scrollTrigger);
		}

		// Second block: 1) svg line draw, 2) footer text in
		if (second) {
			const tl2 = gsap.timeline({
				scrollTrigger: {
					trigger: second,
					start: "top 55%",
					once: true,
				},
			});

			// Line drawing (if path exists)
			if (svgPath && typeof svgPath.getTotalLength === "function") {
				const len = svgPath.getTotalLength();
				gsap.set(svgPath, {
					strokeDasharray: len,
					strokeDashoffset: len,
					opacity: 1,
				});

				tl2.to(svgPath, {
					strokeDashoffset: 0,
					ease: "power2.out",
					scrollTrigger: {
						trigger: svgPath,
						start: "top-=100% 60%",
						end: "bottom+=100% 60%",
						scrub: true,
						// markers: true,
					},
				});
			}

			// Footer text fade/slide
			tl2.from(footer, {
				opacity: 0,
				yPercent: 3,
				duration: 0.6,
				ease: "power2.out",
			});

			this.timelines.push(tl2);
			this.scrollTriggers.push(tl2.scrollTrigger);
		}
	}
}
customElements.define("c-scroll-story", ComponentScrollStory);
// ***END: ComponentScrollStory***

// **START: ComponentNavAccordion***
class ComponentNavAccordion extends HTMLElement {
	constructor() {
		super();
		this.faqEmpty = document.querySelector(".js-faq-empty");
		this.faqGroup = document.querySelectorAll(".js-qa-group");
		this.faqItem = document.querySelectorAll(".js-faq-item");
		this.faqSearchInput = this.querySelector(".js-faq-search-bar");
		this.faqSearchClear = this.querySelector(".js-faq-search-clear");
		this.faqList = []; // list of strings containing formatted FAQ Q+As
		this.observers = []; // Store observers for cleanup
		this.ignoreScrollUpdate = false; // Flag to ignore scroll updates after click
		this.handleSearch = utilsDebounce(this.handleSearch.bind(this), 300); // bind search handler
		this.handleClear = this.handleClear.bind(this);

		// traverse each Q+A pair and add their formatted contents to an array
		for (let item of this.faqItem) {
			let string =
				item.querySelector(".js-accordion-header").innerText +
				item.querySelector(".js-accordion-content").innerText;
			string = formatHandleize(string);
			this.faqList.push(string);
		}
	}
	connectedCallback() {
		this.initNavObserver();
		this.initNavClickHandlers();

		// Search input event listener
		this.faqSearchInput?.addEventListener("input", this.handleSearch);
		this.faqSearchClear?.addEventListener("click", this.handleClear);

		// Prevent form submission on Enter
		this.faqSearchInput?.addEventListener("keydown", (e) => {
			if (e.key === "Enter") {
				e.preventDefault();
			}
		});
	}
	disconnectedCallback() {
		// Clean up all observers
		this.observers.forEach((observer) => observer.disconnect());
		this.observers = [];
		// Remove search input event listener
		this.faqSearchInput?.removeEventListener("input", this.handleSearch);
		this.faqSearchClear?.removeEventListener("click", this.handleClear);
	}

	// Intersection Observer for nav active state
	initNavObserver() {
		if (!this.faqGroup.length) return;

		this.faqGroup.forEach((faq) => {
			const observer = new IntersectionObserver(
				([entry]) => {
					// Ignore updates if user just clicked
					if (this.ignoreScrollUpdate) return;
					if (entry.isIntersecting) {
						this.setActiveNavItem(faq.id, false);
					}
				},
				{
					rootMargin: "-20% 0px -80% 0px",
					threshold: 0,
				}
			);
			observer.observe(faq);
			this.observers.push(observer); // Store for cleanup
		});
	}
	// Set active nav item
	setActiveNavItem(handle, alsoSetBtns = true) {
		const navLinks = this.querySelectorAll(".js-nav-accordion-link");

		// Remove active from all
		navLinks.forEach((item) => item.classList.remove("is-active"));

		// Add active to matching item
		const matchingNav = Array.from(navLinks).find(
			(item) => item.dataset.handle === handle
		);
		matchingNav?.classList.add("is-active");

		if (alsoSetBtns) {
			const navButtons = this.querySelectorAll(".js-nav-accordion-nav-button");
			// Remove active from all buttons
			navButtons.forEach((item) => item.classList.remove("is-active"));
			// Add active to matching button
			const matchingButton = Array.from(navButtons).find(
				(item) => item.dataset.handle === handle
			);
			matchingButton?.classList.add("is-active");
		}
	}

	// Set group active (mobile)
	setActiveGroup(handle) {
		if (!this.faqGroup.length) return;
		// Close all accordions inside all groups
		this.faqGroup.forEach((group) => {
			const accordions = group.querySelectorAll("[data-trigger]");
			const targets = group.querySelectorAll(".c-accordion__target");

			accordions.forEach((accordion) => {
				accordion.classList.remove("is-active");
			});
			targets.forEach((target) => {
				target.classList.remove("is-active");
				target.setAttribute("aria-hidden", true);
			});
		});

		// Remove active from all
		this.faqGroup.forEach((item) => {
			item.classList.remove("is-active");
			item.setAttribute("aria-hidden", true);
		});

		// Add active to matching item
		const matchingFaq = Array.from(this.faqGroup).find(
			(item) => item.id === handle
		);
		matchingFaq?.classList.add("is-active");
		matchingFaq?.setAttribute("aria-hidden", false);
	}

	// Click handlers for nav items
	initNavClickHandlers() {
		const navLinks = this.querySelectorAll(".js-nav-accordion-link");
		const navButtons = this.querySelectorAll(".js-nav-accordion-nav-button");
		if (!navLinks.length && !navButtons.length) return;

		navLinks.forEach((navLink) => {
			navLink.addEventListener("click", () => {
				const handle = navLink.dataset.handle;
				this.setActiveNavItem(handle);

				// Ignore scroll updates for 1 second after click
				this.ignoreScrollUpdate = true;
				setTimeout(() => {
					this.ignoreScrollUpdate = false;
				}, 1000);
			});
		});
		navButtons.forEach((navButton) => {
			navButton.addEventListener("click", () => {
				const handle = navButton.dataset.handle;
				this.setActiveNavItem(handle);
				this.setActiveGroup(handle);
			});
		});
	}
	// Toggle clear button visibility
	toggleClearButton() {
		if (this.faqSearchInput && this.faqSearchClear) {
			this.faqSearchClear.classList.toggle(
				"is-active",
				this.faqSearchInput.value.trim()
			);
			this.faqSearchClear.setAttribute(
				"aria-hidden",
				this.faqSearchInput.value.trim() ? "false" : "true"
			);
			this.faqSearchClear.setAttribute(
				"tabindex",
				this.faqSearchInput.value.trim() ? "0" : "-1"
			);
		}
	}

	// Handle clear button click
	handleClear() {
		if (this.faqSearchInput) {
			this.faqSearchInput.value = "";
			this.faqSearchInput.focus();
			this.toggleClearButton();

			// Reset search results
			this.faqEmpty.classList.add("is-hidden");
			this.faqGroup.forEach((group) => group.classList.remove("is-hidden"));
			// Remove all highlights
			this.faqItem.forEach((item) => {
				item.classList.remove("is-hidden");
				const header = item.querySelector(".js-accordion-header");
				const content = item.querySelector(".js-accordion-content");
				formatRemoveHighlights(header);
				formatRemoveHighlights(content);
			});
		}
	}
	// Search functionality
	handleSearch(e) {
		if (!e.target.matches(".js-faq-search-bar")) return;
		e.preventDefault();

		// Toggle clear button based on input value
		this.toggleClearButton();

		let searchQuery = formatHandleize(e.target.value);

		if (searchQuery) {
			// Hide all groups initially to prevent empty group headers from displaying
			this.faqGroup.forEach((group) => {
				group.classList.add("is-hidden");
			});

			let matchCount = 0;
			let groupHasMatch = new Set(); // Track groups with at least one match

			// First pass: determine matches and track groups
			this.faqItem.forEach((item, i) => {
				const matches = this.faqList[i].includes(searchQuery);
				const groupId = item.closest(".js-qa-group").id;
				const header = item.querySelector(".js-accordion-header");
				const content = item.querySelector(".js-accordion-content");

				if (matches) {
					matchCount++;
					groupHasMatch.add(groupId);
					item.classList.remove("is-hidden");

					// Highlight matching text
					const headerText = header.textContent;
					const contentText = content.textContent;
					header.innerHTML = formatHighlightText(headerText, searchQuery);
					content.innerHTML = formatHighlightText(contentText, searchQuery);
				} else {
					item.classList.add("is-hidden");
					// Remove highlights from non-matching items
					formatRemoveHighlights(header);
					formatRemoveHighlights(content);
				}
			});

			// Second pass: show groups that have matches
			groupHasMatch.forEach((groupId) => {
				const groupWrapper = document.getElementById(groupId);
				groupWrapper?.classList.remove("is-hidden");
			});

			// Handle empty results
			this.faqEmpty.classList.toggle("is-hidden", matchCount > 0);
		} else {
			// Reset to show all
			this.faqEmpty.classList.add("is-hidden");
			this.faqGroup.forEach((group) => group.classList.remove("is-hidden"));
			this.faqItem.forEach((item) => item.classList.remove("is-hidden"));
		}
	}
}
customElements.define("c-nav-accordion", ComponentNavAccordion);
// **END: ComponentNavAccordion**

// ***START: COMBOBOX***
const initComboBox = () => {
	"use strict";

	// Save a list of named combobox actions, for future readability
	const SelectActions = {
		Close: 0,
		CloseSelect: 1,
		First: 2,
		Last: 3,
		Next: 4,
		Open: 5,
		PageDown: 6,
		PageUp: 7,
		Previous: 8,
		Select: 9,
		Type: 10,
	};

	/*
	 * Helper functions
	 */

	// filter an array of options against an input string
	// returns an array of options that begin with the filter string, case-independent
	function filterOptions(options = [], filter, exclude = []) {
		return options.filter((option) => {
			const matches = option.toLowerCase().indexOf(filter.toLowerCase()) === 0;
			return matches && exclude.indexOf(option) < 0;
		});
	}

	// map a key press to an action
	function getActionFromKey(event, menuOpen) {
		const { key, altKey, ctrlKey, metaKey } = event;
		const openKeys = ["ArrowDown", "ArrowUp", "Enter", " "]; // all keys that will do the default open action
		// handle opening when closed
		if (!menuOpen && openKeys.includes(key)) {
			return SelectActions.Open;
		}

		// home and end move the selected option when open or closed
		if (key === "Home") {
			return SelectActions.First;
		}
		if (key === "End") {
			return SelectActions.Last;
		}

		// handle typing characters when open or closed
		if (
			key === "Backspace" ||
			key === "Clear" ||
			(key.length === 1 && key !== " " && !altKey && !ctrlKey && !metaKey)
		) {
			return SelectActions.Type;
		}

		// handle keys when open
		if (menuOpen) {
			if (key === "ArrowUp" && altKey) {
				return SelectActions.CloseSelect;
			} else if (key === "ArrowDown" && !altKey) {
				return SelectActions.Next;
			} else if (key === "ArrowUp") {
				return SelectActions.Previous;
			} else if (key === "PageUp") {
				return SelectActions.PageUp;
			} else if (key === "PageDown") {
				return SelectActions.PageDown;
			} else if (key === "Escape") {
				return SelectActions.Close;
			} else if (key === "Enter" || key === " ") {
				return SelectActions.CloseSelect;
			}
		}
	}

	// return the index of an option from an array of options, based on a search string
	// if the filter is multiple iterations of the same letter (e.g "aaa"), then cycle through first-letter matches
	function getIndexByLetter(options, filter, startIndex = 0) {
		const orderedOptions = [
			...options.slice(startIndex),
			...options.slice(0, startIndex),
		];
		const firstMatch = filterOptions(orderedOptions, filter)[0];
		const allSameLetter = (array) =>
			array.every((letter) => letter === array[0]);

		// first check if there is an exact match for the typed string
		if (firstMatch) {
			return options.indexOf(firstMatch);
		}

		// if the same letter is being repeated, cycle through first-letter matches
		else if (allSameLetter(filter.split(""))) {
			const matches = filterOptions(orderedOptions, filter[0]);
			return options.indexOf(matches[0]);
		}

		// if no matches, return -1
		else {
			return -1;
		}
	}

	// get an updated option index after performing an action
	function getUpdatedIndex(currentIndex, maxIndex, action) {
		const pageSize = 10; // used for pageup/pagedown

		switch (action) {
			case SelectActions.First:
				return 0;
			case SelectActions.Last:
				return maxIndex;
			case SelectActions.Previous:
				return Math.max(0, currentIndex - 1);
			case SelectActions.Next:
				return Math.min(maxIndex, currentIndex + 1);
			case SelectActions.PageUp:
				return Math.max(0, currentIndex - pageSize);
			case SelectActions.PageDown:
				return Math.min(maxIndex, currentIndex + pageSize);
			default:
				return currentIndex;
		}
	}

	// check if element is visible in browser view port
	function isElementInView(element) {
		var bounding = element.getBoundingClientRect();

		return (
			bounding.top >= 0 &&
			bounding.left >= 0 &&
			bounding.bottom <=
				(window.innerHeight || document.documentElement.clientHeight) &&
			bounding.right <=
				(window.innerWidth || document.documentElement.clientWidth)
		);
	}

	// check if an element is currently scrollable
	function isScrollable(element) {
		return element && element.clientHeight < element.scrollHeight;
	}

	// check if on mobile or tablet device
	function isMobileOrTablet() {
		const userAgent = navigator.userAgent.toLowerCase();

		return (
			/iPhone|Android/i.test(navigator.userAgent) ||
			/(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(
				userAgent
			)
		);
	}

	// ensure a given child element is within the parent's visible scroll area
	// if the child is not visible, scroll the parent
	function maintainScrollVisibility(activeElement, scrollParent) {
		const { offsetHeight, offsetTop } = activeElement;
		const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent;

		const isAbove = offsetTop < scrollTop;
		const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight;

		if (isAbove) {
			scrollParent.scrollTo(0, offsetTop);
		} else if (isBelow) {
			scrollParent.scrollTo(0, offsetTop - parentOffsetHeight + offsetHeight);
		}
	}

	/*
	 * Select Component
	 * Accepts a combobox element and an array of string options
	 */
	const Select = function (el, options = []) {
		// element refs
		this.el = el;
		this.selector = this.el.querySelector("select");
		this.optionNodes = Array.from(this.selector.querySelectorAll("option"));
		this.comboBox = el.querySelector(".combobox");
		this.comboEl = document.createElement("div");
		this.listboxEl = document.createElement("div");

		this.comboEl.setAttribute("aria-controls", `${this.el.id}`);
		this.comboEl.setAttribute("aria-expanded", false);
		this.comboEl.setAttribute("aria-haspopup", "listbox");
		this.comboEl.setAttribute("aria-labelledby", `${this.el.id}`);
		this.comboEl.setAttribute("role", "combobox");

		this.comboEl.id = `${this.el.id}-input`;
		this.comboEl.classList.add("combobox-input");
		this.comboBox.prepend(this.comboEl);

		this.listboxEl.setAttribute("aria-labelledby", `${this.el.id}`);
		this.listboxEl.setAttribute("role", "listbox");
		this.listboxEl.setAttribute("tabindex", "-1");
		this.listboxEl.id = `${this.el.id}-listbox`;
		this.listboxEl.classList.add("combobox-menu");

		// data
		this.idBase = this.comboEl.id || "combo";
		this.options = options;

		// If there are multiple options, show the custom listbox dropdown above the combo input.
		// Otherwise (1 or 0 options), visually disable the input to indicate it's not interactive.
		if (options.length > 1) {
			this.comboEl.setAttribute("tabindex", 0);
			this.comboBox.prepend(this.listboxEl);
		} else {
			this.comboBox.classList.add("is-disabled");
		}

		// state
		this.activeIndex = this.optionNodes.findIndex((option) => option.selected);
		// fallback to 0 if no match
		if (this.activeIndex == -1) this.activeIndex = 0;
		this.open = false;
		this.searchString = "";
		this.searchTimeout = null;

		// init
		if (el && this.comboEl && this.listboxEl) {
			this.init();

			// assign ID to <label> and <select>
			this.el
				.querySelector("label")
				.setAttribute("for", `${this.el.id}-select`);
			this.selector.id = `${this.el.id}-select`;

			// Comment out to allow the change event to propagate to "change", ".c-product-form .js-variant-selector"
			// // match <select> actions to combobox
			// this.selector.addEventListener("change", (event) => {
			// 	event.stopPropagation();
			// 	this.onOptionClick(this.selector.selectedIndex);
			// });

			// Comment out to enable combobox on mobile
			// if (isMobileOrTablet()) {
			// 	this.listboxEl.setAttribute("aria-hidden", true);
			// } else {
			this.el.classList.add("is-select-hidden");
			// }
		}
	};

	Select.prototype.init = function () {
		// select "selected" option by default, or first index
		const defaultOption =
			this.optionNodes.find((option) => option.selected) || this.options[0];

		if (defaultOption) {
			this.comboEl.textContent = defaultOption.textContent;
		} else {
			this.comboEl.textContent = "";
		}

		// add event listeners
		this.comboEl.addEventListener("blur", this.onComboBlur.bind(this));
		this.comboEl.addEventListener("click", this.onComboClick.bind(this));
		this.comboEl.addEventListener("keydown", this.onComboKeyDown.bind(this));

		// create options
		this.options.map((option, index) => {
			const optionEl = this.createOption(option, index);
			this.listboxEl.appendChild(optionEl);
		});
	};

	Select.prototype.createOption = function (optionText, index) {
		const optionEl = document.createElement("div");
		const isSelected = this.optionNodes[index].selected ? true : false;
		optionEl.setAttribute("role", "option");
		this.el.classList.contains("js-fetch-product") &&
			optionEl.setAttribute(
				"data-product-url",
				this.optionNodes[index].dataset.productUrl
			);
		optionEl.id = `${this.idBase}-${index}`;
		optionEl.className = isSelected
			? "combobox-option is-option-current"
			: "combobox-option";
		this.optionNodes[index].disabled && optionEl.classList.add("is-disabled");
		optionEl.setAttribute("aria-selected", `${isSelected}`);
		optionEl.innerText = optionText;

		optionEl.addEventListener("click", (event) => {
			event.stopPropagation();
			this.onOptionClick(index);
		});
		optionEl.addEventListener("mousedown", this.onOptionMouseDown.bind(this));

		return optionEl;
	};

	Select.prototype.getSearchString = function (char) {
		// reset typing timeout and start new timeout
		// this allows us to make multiple-letter matches, like a native select
		if (typeof this.searchTimeout === "number") {
			window.clearTimeout(this.searchTimeout);
		}

		this.searchTimeout = window.setTimeout(() => {
			this.searchString = "";
		}, 500);

		// add most recent letter to saved search string
		this.searchString += char;
		return this.searchString;
	};

	Select.prototype.onComboBlur = function () {
		// do not do blur action if ignoreBlur flag has been set
		if (this.ignoreBlur) {
			this.ignoreBlur = false;
			return;
		}

		// select current option and close
		if (this.open) {
			this.selectOption(this.activeIndex);
			this.updateMenuState(false, false);
		}
	};

	Select.prototype.onComboClick = function () {
		this.updateMenuState(!this.open, false);
	};

	Select.prototype.onComboKeyDown = function (event) {
		const { key } = event;
		const max = this.options.length - 1;

		const action = getActionFromKey(event, this.open);

		switch (action) {
			case SelectActions.Last:
			case SelectActions.First:
				this.updateMenuState(true);
			// intentional fallthrough

			case SelectActions.Next:
			case SelectActions.Previous:
			case SelectActions.PageUp:
			case SelectActions.PageDown:
				event.preventDefault();
				return this.onOptionChange(
					getUpdatedIndex(this.activeIndex, max, action)
				);
			case SelectActions.CloseSelect:
				event.preventDefault();
				this.selectOption(this.activeIndex);
			// intentional fallthrough

			case SelectActions.Close:
				event.preventDefault();
				return this.updateMenuState(false);
			case SelectActions.Type:
				return this.onComboType(key);
			case SelectActions.Open:
				event.preventDefault();
				return this.updateMenuState(true);
		}
	};

	Select.prototype.onComboType = function (letter) {
		// open the listbox if it is closed
		this.updateMenuState(true);

		// find the index of the first matching option
		const searchString = this.getSearchString(letter);
		const searchIndex = getIndexByLetter(
			this.options,
			searchString,
			this.activeIndex + 1
		);

		// if a match was found, go to it
		if (searchIndex >= 0) {
			this.onOptionChange(searchIndex);
		}
		// if no matches, clear the timeout and search string
		else {
			window.clearTimeout(this.searchTimeout);
			this.searchString = "";
		}
	};

	Select.prototype.onOptionChange = function (index) {
		// update state
		this.activeIndex = index;

		// update aria-activedescendant
		this.comboEl.setAttribute(
			"aria-activedescendant",
			`${this.idBase}-${index}`
		);

		// update active option styles
		const options = this.el.querySelectorAll("[role=option]");
		[...options].forEach((optionEl) => {
			optionEl.classList.remove("is-option-current");
		});
		options[index].classList.add("is-option-current");

		// ensure the new option is in view
		if (isScrollable(this.listboxEl)) {
			maintainScrollVisibility(options[index], this.listboxEl);
		}

		// ensure the new option is visible on screen
		// ensure the new option is in view
		if (!isElementInView(options[index])) {
			options[index].scrollIntoView({ behavior: "smooth", block: "nearest" });
		}

		// Custom event for external detection
		const event = new CustomEvent("combobox-change", {
			detail: {
				index: index,
				value: this.options[index],
				url: this.optionNodes[index].dataset?.productUrl,
			},
		});
		this.comboEl.dispatchEvent(event);
	};

	Select.prototype.onOptionClick = function (index) {
		this.onOptionChange(index);
		this.selectOption(index);
		this.updateMenuState(false);
	};

	Select.prototype.onOptionMouseDown = function () {
		// Clicking an option will cause a blur event,
		// but we don't want to perform the default keyboard blur action
		this.ignoreBlur = true;
	};

	Select.prototype.selectOption = function (index) {
		// update state
		this.activeIndex = index;

		// update displayed value
		const selected = this.options[index];
		this.comboEl.innerHTML = selected;

		// update aria-selected
		const options = this.el.querySelectorAll("[role=option]");
		[...options].forEach((optionEl) => {
			optionEl.setAttribute("aria-selected", "false");
		});
		options[index].setAttribute("aria-selected", "true");

		// update <select> & options
		this.selector.value = this.optionNodes[index].value;
		this.optionNodes.forEach((option, i) => {
			// option.classList.toggle("is-selected", i == index);
			if (i == index) {
				option.setAttribute("selected", true);
			} else {
				option.removeAttribute("selected");
			}
		});

		// 🔥 trigger native <select> change so external form listeners catch it
		this.selector.dispatchEvent(new Event("change", { bubbles: true }));
	};

	Select.prototype.updateMenuState = function (open, callFocus = true) {
		if (this.open === open) {
			return;
		}

		// update state
		this.open = open;

		// update aria-expanded and styles
		this.comboEl.setAttribute("aria-expanded", `${open}`);
		open
			? this.el.classList.add("is-open")
			: this.el.classList.remove("is-open");

		// update activedescendant
		const activeID = open ? `${this.idBase}-${this.activeIndex}` : "";
		this.comboEl.setAttribute("aria-activedescendant", activeID);

		if (activeID === "" && !isElementInView(this.comboEl)) {
			this.comboEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
		}

		// move focus back to the selector, if needed
		callFocus && this.comboEl.focus();
	};

	// init select
	document.querySelectorAll(".combobox-selector").forEach((el) => {
		let options = [];

		el.querySelectorAll("option").forEach((option) => {
			options.push(option.innerText);
		});

		new Select(el, options);
	});
};
// ***END: COMBOBOX***

// ***START: ComponentDataList***
class ComponentDataList extends HTMLElement {
	constructor() {
		super();
		this.style.display = "block";

		this.bodyEl = this.querySelector(".js-data-list__body");
		this.loadMoreBtn = this.querySelector("[data-load-more]");
		this.textMore = this.querySelector("[data-text-more]");
		this.textLess = this.querySelector("[data-text-less]");
		this.rows = this.querySelectorAll(".js-data-list__row");

		this.toShow = parseInt(this.bodyEl?.dataset.toShow) || 5;
		this.toLoad = parseInt(this.bodyEl?.dataset.toLoad) || 5;
		this.total = this.rows.length;
		this.currentlyShowing = this.toShow;
		this.isExpanded = false;
	}

	connectedCallback() {
		if (!this.bodyEl || !this.loadMoreBtn) return;

		this.updateHeight();
		this.loadMoreBtn.addEventListener("click", this.handleLoadMore.bind(this));

		// Handle resize
		window.addEventListener("resize", this.updateHeight.bind(this));
	}

	disconnectedCallback() {
		this.loadMoreBtn?.removeEventListener(
			"click",
			this.handleLoadMore.bind(this)
		);
		window.removeEventListener("resize", this.updateHeight.bind(this));
	}

	updateHeight() {
		if (!this.bodyEl || !this.rows.length) return;

		// Remove all is-visible classes first
		this.rows.forEach((row) => {
			row.classList.remove("is-visible", "is-last-visible");
			row.setAttribute("aria-hidden", true);
			row.setAttribute("tabindex", -1);
		});

		let height = 0;
		let lastVisibleIndex = -1;
		for (
			let i = 0;
			i < Math.min(this.currentlyShowing, this.rows.length);
			i++
		) {
			height += this.rows[i].offsetHeight;
			this.rows[i].classList.add("is-visible");
			this.rows[i].removeAttribute("aria-hidden");
			this.rows[i].removeAttribute("tabindex");
			lastVisibleIndex = i;
		}

		// Add last-visible class to the last visible row (for border-top)
		if (lastVisibleIndex >= 0) {
			this.rows[lastVisibleIndex].classList.add("is-last-visible");
		}

		this.bodyEl.style.maxHeight = height + "px";

		// Hide button if all items fit initially (before user has expanded)
		if (this.total <= this.toShow) {
			this.loadMoreBtn.style.display = "none";
			return;
		}

		// Show button and toggle text based on state
		this.loadMoreBtn.style.display = "";

		// Toggle button text based on state
		if (this.currentlyShowing >= this.total) {
			this.isExpanded = true;
			this.textMore.classList.remove("is-visible");
			this.textLess.classList.add("is-visible");
		} else {
			this.isExpanded = false;
			this.textMore.classList.add("is-visible");
			this.textLess.classList.remove("is-visible");
		}
	}

	handleLoadMore() {
		if (this.isExpanded) {
			// Collapse back to initial state
			this.currentlyShowing = this.toShow;
		} else {
			// Expand to show more
			this.currentlyShowing += this.toLoad;
		}
		this.updateHeight();
	}
}
customElements.define("c-data-list", ComponentDataList);
//  ***END: ComponentDataList***

const initGSAP = () => {
	gsap.registerPlugin(MorphSVGPlugin, ScrollTrigger, ScrollToPlugin);
};

const initHeader = () => {
	const allToggles = document.querySelectorAll(".js-megamenu-toggle");
	const allMegamenu = document.querySelectorAll(".js-megamenu");
	const mobileMenu = document.querySelector(".js-mobile-menu");
	const overlay = document.querySelector(".js-g-overlay");
	const root = document.documentElement;

	const toggleMobileMenu = (isActive = false) => {
		root.classList.toggle("is-mobile-menu-active", isActive);
		mobileMenu.classList.toggle("is-active", isActive);
		mobileMenu.setAttribute("aria-hidden", !isActive);

		// set first accordion to active
		const firstAccordion = mobileMenu.querySelector("[data-trigger]");
		if (firstAccordion && isActive) {
			!firstAccordion.classList.contains("is-active") && firstAccordion.click();
		}
	};

	const toggleOverlay = (isActive = false) => {
		overlay.setAttribute("tabindex", isActive ? 0 : -1);
	};

	const closeAllMegamenu = () => {
		scrollEnable();
		root.classList.remove("is-megamenu-active");
		toggleMobileMenu();

		// Loop through all megamenus and reset their state
		allMegamenu.forEach((menu) => {
			menu.classList.remove("is-active");
			menu.setAttribute("aria-hidden", true);

			// Reset menu content (trigger mouseover on the first trigger)
			const firstTrigger = menu.querySelector(".js-megamenu-content-trigger");
			if (firstTrigger) {
				const mouseOverEvent = new MouseEvent("mouseover", {
					bubbles: true,
					cancelable: true,
					view: window,
				});
				firstTrigger.dispatchEvent(mouseOverEvent);
			}
			// Set the active content's aria-hidden to true
			const activeContent = menu.querySelector(
				".js-megamenu-content.is-active"
			);
			if (activeContent) {
				activeContent.setAttribute("aria-hidden", true);
			}
		});

		// Reset all toggles
		allToggles.forEach((toggle) => {
			toggle.classList.remove("is-active");
			toggle.setAttribute("aria-expanded", false);
		});

		// Close overlay
		toggleOverlay();
	};

	const closeAllDropdowns = () => {
		Array.from(document.querySelectorAll(".js-linklist-dropdown")).forEach(
			(dropdown) => {
				dropdown.classList.remove("is-active");
				dropdown.setAttribute("aria-hidden", true);
			}
		);
	};
	const toggleActiveMenu = (targetMenu) => {
		let anyMenuActive = false;

		// Find the currently active menu (if any)
		const currentlyActiveMenu = Array.from(allMegamenu).find((menu) =>
			menu.classList.contains("is-active")
		);

		if (currentlyActiveMenu?.dataset.megamenu === targetMenu) return;
		Array.from(allMegamenu).forEach((menu) => {
			const content = menu.querySelector(".js-megamenu-content");
			const items = content
				? content.querySelectorAll(".js-megamenu-item")
				: [];

			if (menu.dataset.megamenu === targetMenu) {
				menu.classList.add("is-active");
				menu.setAttribute("aria-hidden", false);
				if (content) content.setAttribute("aria-hidden", false);

				anyMenuActive = true;

				// animate items in
				if (items.length) {
					gsap.fromTo(
						items,
						{ opacity: 0, y: 50 },
						{
							opacity: 1,
							y: 0,
							stagger: 0.03,
							duration: 0.08,
							ease: "power2.out",
							clearProps: "all",
						}
					);
				}
			} else {
				if (menu.classList.contains("is-active")) {
					// animate items out before hiding
					if (items.length) {
						gsap.to(items, {
							opacity: 0,
							y: 50,
							stagger: 0.03,
							duration: 0.08,
							ease: "power1.in",
							onComplete: () => {
								menu.classList.remove("is-active");
								menu.setAttribute("aria-hidden", true);
								if (content) content.setAttribute("aria-hidden", true);
							},
						});
					} else {
						menu.classList.remove("is-active");
						menu.setAttribute("aria-hidden", true);
						if (content) content.setAttribute("aria-hidden", true);
					}
				} else {
					menu.classList.remove("is-active");
					menu.setAttribute("aria-hidden", true);
					if (content) content.setAttribute("aria-hidden", true);
				}
			}
		});

		// Update toggles
		Array.from(allToggles).forEach((toggle) => {
			const isTargetActive =
				toggle.dataset.megamenu === targetMenu && anyMenuActive;
			toggle.classList.toggle("is-active", isTargetActive);
			toggle.setAttribute("aria-expanded", isTargetActive);
		});

		// Manage scroll and class states
		if (anyMenuActive) {
			scrollDisable();
			root.classList.add("is-megamenu-active");
			toggleMobileMenu();
			toggleOverlay(true);
		} else {
			scrollEnable();
			root.classList.remove("is-megamenu-active");
			toggleMobileMenu();
			toggleOverlay();
		}
		closeAllDropdowns();
	};

	const toggleDropDown = (trigger, shouldOpen) => {
		const dropdown = trigger.querySelector(".js-linklist-dropdown");
		if (dropdown) {
			dropdown.classList.toggle("is-active", shouldOpen);
			dropdown.setAttribute(
				"aria-hidden",
				!dropdown.classList.contains("is-active")
			);
			closeAllMegamenu();
		}
	};

	const handleMenuContentInteraction = (e) => {
		const { target } = e;
		const wrapper = target.closest(".js-megamenu-content-wrapper");
		if (!wrapper) return;

		const content = wrapper.querySelector(".js-megamenu-content");
		const menu = target.closest(".js-megamenu");
		if (!content || !menu) return;

		if (content.classList.contains("is-active")) return;

		// Close the previously active item
		const prevTrigger = menu.querySelector(
			".js-megamenu-content-trigger.is-active"
		);
		const prevContent = menu.querySelector(".js-megamenu-content.is-active");

		if (prevTrigger) prevTrigger.classList.remove("is-active");
		if (prevContent) {
			// animate items out
			const prevItems = prevContent.querySelectorAll(".js-megamenu-item");
			if (prevItems.length) {
				gsap.to(prevItems, {
					opacity: 0,
					y: 50,
					stagger: 0.03,
					duration: 0.08,
					onComplete: () => {
						prevContent.classList.remove("is-active");
						prevContent.setAttribute("aria-hidden", true);
					},
				});
			} else {
				prevContent.classList.remove("is-active");
				prevContent.setAttribute("aria-hidden", true);
			}
		}

		// Activate the current item
		target.classList.add("is-active");
		content.classList.add("is-active");
		content.setAttribute("aria-hidden", false);

		// animate items in
		const items = content.querySelectorAll(".js-megamenu-item");
		if (items.length) {
			gsap.fromTo(
				items,
				{ opacity: 0, y: 50 },
				{ opacity: 1, y: 0, stagger: 0.03, duration: 0.08, ease: "power2.out" }
			);
		}
	};

	// Update collection link based on active accordion target
	const updateCollectionLink = () => {
		const activeAccordionTarget = document.querySelector(
			".js-accordion-target.is-active"
		);
		const collectionLink = document.querySelector(".js-collection-link");

		if (activeAccordionTarget && collectionLink) {
			const collectionUrl = activeAccordionTarget.dataset.collectionLink;
			if (collectionUrl) {
				collectionLink.href = collectionUrl;
			}
		}
	};

	// Event listeners
	on("body", "mouseover", ".js-linklist-dropdown-toggle", (e) => {
		const trigger = e.target.closest(".js-linklist-dropdown-toggle");
		toggleDropDown(trigger, true);
	});
	on("body", "mouseout", ".js-linklist-dropdown-toggle", (e) => {
		const trigger = e.target.closest(".js-linklist-dropdown-toggle");
		toggleDropDown(trigger, false);
	});

	on("body", "click", ".js-mobile-menu-toggle", () => {
		const isMobileMenuActive = !root.classList.contains(
			"is-mobile-menu-active"
		);

		scrollDisable();
		toggleMobileMenu(isMobileMenuActive);
		toggleOverlay(isMobileMenuActive);

		if (!isMobileMenuActive) {
			closeAllMegamenu();
			toggleOverlay();
		}
	});

	on("body", "click", ".js-megamenu-toggle", (e) => {
		const targetMenu = e.target.closest(".js-megamenu-toggle").dataset.megamenu;
		toggleActiveMenu(targetMenu);
	});

	on("body", "mouseover", ".js-megamenu-toggle", (e) => {
		if (!vs.isTabletScreen) {
			// Guard: Don't trigger if we're hovering over the megamenu content itself
			// (since megamenu is now nested inside .js-megamenu-toggle)
			if (e.target.closest(".js-megamenu")) return;

			const toggle = e.target.closest(".js-megamenu-toggle");
			if (!toggle) return;

			const targetMenu = toggle.dataset.megamenu;
			if (!targetMenu) return;

			toggleActiveMenu(targetMenu);
		}
	});

	on("body", "mouseover", ".js-megamenu-content-trigger", (e) => {
		handleMenuContentInteraction(e);
	});

	on("body", "click", ".js-megamenu-content-trigger", (e) => {
		handleMenuContentInteraction(e);
	});

	on("body", "transitionend", ".js-accordion-target", (e) => {
		if (e.target.classList.contains("is-active")) {
			updateCollectionLink();
		}
	});

	// Keyboard navigation
	let isUsingTabNavigation = false;
	on("body", "keydown", "body", (e) => {
		if (e.key == "Escape") {
			closeAllMegamenu();
		} else if (e.key === "Tab") {
			isUsingTabNavigation = true;
		}
	});

	// Overlay handlers
	on("body", "mouseover", ".js-g-overlay, .js-megamenu-close", () => {
		if (!isUsingTabNavigation) {
			closeAllMegamenu();
		}
	});

	on("body", "click", ".js-g-overlay, .js-megamenu-close", () => {
		closeAllMegamenu();
	});

	// Dropdown
	on("body", "focusin", ".js-linklist-dropdown-toggle > a", (e) => {
		const trigger = e.target.closest(".js-linklist-dropdown-toggle");
		toggleDropDown(trigger, true);
	});
	on("body", "focusout", ".js-linklist-dropdown-toggle", (e) => {
		const trigger = e.target.closest(".js-linklist-dropdown-toggle");

		// Check if the focus moved outside the parent container (trigger + dropdown)
		if (trigger && (!e.relatedTarget || !trigger.contains(e.relatedTarget))) {
			toggleDropDown(trigger, false);
		}
	});
	// Megamenu
	on("body", "focusin", ".js-megamenu-toggle", (e) => {
		// Guard: Don't trigger if we're hovering over the megamenu content itself
		// (since megamenu is now nested inside .js-megamenu-toggle)
		if (e.target.closest(".js-megamenu")) return;
		const targetMenu = e.target.closest(".js-megamenu-toggle").dataset.megamenu;
		toggleActiveMenu(targetMenu);
	});
	on("body", "focusout", ".js-megamenu-toggle", (e) => {
		const toggle = e.target.closest(".js-megamenu-toggle");
		// Check if the focus moved outside the parent container (toggle + megamenu)
		if (toggle && (!e.relatedTarget || !toggle.contains(e.relatedTarget))) {
			closeAllMegamenu();
		}
	});
};

const initLanding = () => {
	const landing = document.querySelector(".js-landing");
	if (!landing) return;
	landing.classList.add("is-ready");

	const landingLogo = landing.querySelector(".js-landing-logo");
	const landingBgMedia = landing.querySelector(".js-landing-bg-media");
	const landingDeco = landing.querySelector(".js-landing-deco");
	const landingMask = landing.querySelector(".js-landing-mask");
	const landingMaskTop = landing.querySelector(".js-landing-mask-top");
	const landingIntro = landing.querySelector(".js-landing-intro");
	const header = document.querySelector(".g-header");
	const nextSection = landing.nextElementSibling;

	const hasPlayedInSession =
		sessionStorage.getItem("celticLandingAnimationPlayed") === "true";

	const getPixelCustomProperty = (propertyName, fallbackValue = 0) => {
		const propertyValue = parseFloat(
			getComputedStyle(root).getPropertyValue(propertyName)
		);
		return Number.isFinite(propertyValue) ? propertyValue : fallbackValue;
	};

	const getLandingLogoTargetY = () => {
		if (!landingLogo || !vs.isMobileDown) return "-10%";

		const logoTop = landingLogo.getBoundingClientRect().top;
		const headerBottom = header?.getBoundingClientRect().bottom || 0;
		const headerTopOffset = getPixelCustomProperty("--s-header-top-offset");
		const logoGap = getPixelCustomProperty("--s-24", 24);

		return headerBottom + headerTopOffset + logoGap - logoTop;
	};

	const _playAnimation = () => {
		if (hasPlayedInSession) {
			root.classList.add("is-landing-played");
			return;
		}
		// Mark animation as played in this session
		sessionStorage.setItem("celticLandingAnimationPlayed", "true");

		scrollHandler.scrollDisable();
		const scaler =
			parseFloat(getComputedStyle(landing).getPropertyValue("--mask-scaler")) ||
			1;
		const scalerOffset = 0.1; // to prevent initial flicker

		const tl = gsap.timeline({
			defaults: { duration: 4, ease: "power4.inOut", force3D: true },
			onComplete: () => {
				scrollHandler.scrollEnable();
				root.classList.add("is-landing-played");

				if (heavyAnimatedElements.length) {
					gsap.set(heavyAnimatedElements, {
						clearProps: "willChange",
					});
				}
			},
		});

		// Optimize performance by hinting the browser about upcoming transforms
		const heavyAnimatedElements = [
			landingDeco,
			landingBgMedia,
			landingLogo,
			landingIntro,
			header,
		].filter(Boolean);

		if (heavyAnimatedElements.length) {
			gsap.set(heavyAnimatedElements, {
				willChange: "transform",
			});
		}
		if (vs.browserType() === "Safari") tl.delay(1);

		if (landingMask) {
			tl.fromTo(
				landingMask,
				{ scale: 1 / (scaler - scalerOffset), rotation: 0.01 },
				{ scale: 1, rotation: 0.01 },
				1
			);
		}
		if (landingMaskTop) {
			tl.fromTo(
				landingMaskTop,
				{ scale: 1 / (scaler - scalerOffset), rotation: 0.01 },
				{ scale: 1, rotation: 0.01 },
				"<"
			);
		}
		if (landingDeco) {
			tl.fromTo(
				landingDeco,
				{ scale: 1, rotation: 0.01 },
				{ scale: 2, rotation: 0.01 },
				"<"
			);
			const decoLeft = landingDeco.querySelector(".js-landing-deco-left");
			const decoRight = landingDeco.querySelector(".js-landing-deco-right");
			if (decoLeft) {
				tl.fromTo(
					decoLeft,
					{ xPercent: 0, yPercent: 0 },
					{ xPercent: -50, yPercent: 60 },
					"<"
				);
			}
			if (decoRight) {
				tl.fromTo(
					decoRight,
					{ xPercent: 0, yPercent: 0 },
					{ xPercent: 50, yPercent: 60 },
					"<"
				);
			}
		}

		if (landingBgMedia) {
			tl.fromTo(
				landingBgMedia,
				{ scale: 1, rotation: 0.01 },
				{ scale: 1.1, rotation: 0.01 },
				"<"
			);
		}
		if (landingLogo) {
			tl.fromTo(
				landingLogo,
				{ y: 0, scale: 0.8 },
				{
					y: getLandingLogoTargetY(),
					scale: 1,
				},
				"<"
			);
		}

		if (header) {
			const headerMenuWrapper = header.querySelector(".g-header__menu-wrapper");
			const topOffset =
				parseFloat(
					getComputedStyle(header).getPropertyValue("--s-header-top-offset")
				) || 0;
			const initialY = -(header.offsetHeight + topOffset * 2);
			if (headerMenuWrapper) gsap.set(headerMenuWrapper, { boxShadow: "none" }); // prevent box-shadow during animation
			tl.fromTo(
				header,
				{ y: initialY, rotation: 0.01 },
				{
					y: 0,
					rotation: 0.01,
					duration: 2,
					onComplete: () => {
						if (headerMenuWrapper)
							gsap.set(headerMenuWrapper, { clearProps: "boxShadow" });
					},
				},
				"-=3"
			);
		}
		if (landingIntro) {
			tl.fromTo(
				landingIntro,
				{ yPercent: 10, autoAlpha: 0 },
				{ yPercent: 0, autoAlpha: 1, duration: 2 },
				"<"
			);
		}
		if (nextSection) {
			tl.fromTo(
				nextSection,
				{
					yPercent: 10,
				},
				{
					yPercent: 0,
					duration: 2,
				},
				"<"
			);
		}
		tl.then(() => scrollHandler.scrollEnable());
	};
	const _applyAnimationEndState = () => {
		// Decorative group
		if (landingDeco) {
			landingDeco.style.display = "none";
		}

		// Background media (final subtle zoom)
		if (landingBgMedia) {
			landingBgMedia.style.transform = "scale(1.1, 1.1)";
		}

		// Logo position.
		if (landingLogo) {
			const targetY = getLandingLogoTargetY();
			landingLogo.style.transform = `translateY(${
				typeof targetY === "number" ? `${targetY}px` : targetY
			})`;
		}

		// Add class if animation was skipped (already played or aborted)
		setTimeout(() => {
			// add a slight delay for cookie banner to populate first
			root.classList.add("is-landing-played");
		}, 1000);
	};

	// Abort landing animation setup when:
	// 1) GSAP isn't loaded (prevents runtime errors)
	// 2) User has 'prefers-reduced-motion: reduce' (respect accessibility)
	// 3) Landing is not first section (prevents animation when landing is used mid-page)
	// 4) Animation already played in this session
	const main = document.querySelector("#main");
	if (
		typeof gsap === "undefined" ||
		window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
		main?.firstElementChild !== landing ||
		hasPlayedInSession
	) {
		_applyAnimationEndState();
		return;
	}

	// 4) Check if landing is already in viewport on page load
	const rect = landing.getBoundingClientRect();
	const isInViewport = rect.top < window.innerHeight && rect.bottom >= 0;
	const isNotAtTop = window.scrollY > 0; // Check if user has scrolled down

	if (!isInViewport || isNotAtTop) {
		// Landing section is not visible OR user didn't start from top, skip animation and apply end state
		_applyAnimationEndState();
		return;
	}

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					_playAnimation();
					observer.unobserve(entry.target);
				}
			});
		},
		{
			threshold: 0.35,
		}
	);
	observer.observe(landing);
};

const init404 = () => {
	const container = document.querySelector(".js-404");
	if (!container) return;

	const notFoundTitle = container.querySelector(".js-404-title");
	const notFoundBgMedia = container.querySelector(".js-404-bg");
	const notFoundDecos = container.querySelector(".js-404-decos");
	const notFoundMask = container.querySelector(".js-404-mask");
	const notFoundMaskTop = container.querySelector(".js-404-mask-top");
	const notFoundContent = container.querySelector(".js-404-content");

	const hasPlayedInSession =
		sessionStorage.getItem("celtic404AnimationPlayed") === "true";

	const _playAnimation = () => {
		if (hasPlayedInSession) return;
		// Mark animation as played in this session
		sessionStorage.setItem("celtic404AnimationPlayed", "true");
		scrollHandler.scrollDisable();

		const scaler =
			parseFloat(
				getComputedStyle(container).getPropertyValue("--mask-scaler")
			) || 1;
		const scalerOffset = 0.1; // to prevent initial flicker

		const tl = gsap.timeline({
			defaults: { duration: 4, ease: "power4.inOut", force3D: true },
		});

		// Optimize performance by hinting the browser about upcoming transforms
		const heavyAnimatedElements = [
			notFoundTitle,
			notFoundBgMedia,
			notFoundDecos,
			notFoundContent,
		].filter(Boolean);

		if (heavyAnimatedElements.length) {
			gsap.set(heavyAnimatedElements, {
				willChange: "transform",
			});
		}

		if (vs.browserType() === "Safari") tl.delay(1);

		if (notFoundMask) {
			tl.fromTo(
				notFoundMask,
				{ scale: 1, rotation: 0.01 },
				{ scale: 1 / (scaler - scalerOffset), rotation: 0.01 },
				1
			);
		}
		if (notFoundMaskTop) {
			tl.fromTo(
				notFoundMaskTop,
				{ scale: 1, rotation: 0.01 },
				{ scale: 1 / (scaler - scalerOffset), rotation: 0.01, delay: 0.2 },
				"<"
			);
		}
		if (notFoundBgMedia) {
			tl.fromTo(
				notFoundBgMedia,
				{ scale: 1.2, rotation: 0.01 },
				{ scale: 1, rotation: 0.01 },
				"<"
			);
			if (notFoundDecos) {
				const notFoundDecoLefts =
					notFoundDecos.querySelectorAll(".js-404-deco-left");
				const notFoundDecoRights =
					notFoundDecos.querySelectorAll(".js-404-deco-right");
				if (notFoundDecoLefts.length) {
					tl.fromTo(
						notFoundDecoLefts,
						{ xPercent: 0, yPercent: 0 },
						{ xPercent: -30, yPercent: 20, stagger: 0.1 },
						"<"
					);
				}
				if (notFoundDecoRights.length) {
					tl.fromTo(
						notFoundDecoRights,
						{ xPercent: 0, yPercent: 0 },
						{ xPercent: 30, yPercent: 20, stagger: 0.1 },
						"<"
					);
				}
			}
		}

		if (notFoundTitle) {
			tl.fromTo(
				notFoundTitle,
				{ scale: 1.5, rotation: 0.01 },
				{ scale: 1, rotation: 0.01, delay: 0.2 },
				"<"
			);
		}
		if (notFoundContent) {
			tl.fromTo(notFoundContent, { yPercent: 150 }, { yPercent: 0 }, "<");
		}

		tl.then(() => scrollHandler.scrollEnable());
	};

	const _applyAnimationEndState = () => {
		const scaler =
			parseFloat(
				getComputedStyle(container).getPropertyValue("--mask-scaler")
			) || 1;
		if (notFoundDecos) notFoundDecos.classList.add("is-hidden");
		if (notFoundMask)
			notFoundMask.style.transform = `translate3d(-50%, -50%, 0) scale(${
				1 / scaler
			})`;
		if (notFoundMaskTop)
			notFoundMaskTop.style.transform = `translate3d(-50%, -50%, 0) scale(${
				1 / scaler
			})`;
		if (notFoundTitle) {
			notFoundTitle.style.transform = "translate3d(-50%, -60%, 0)";
		}
	};

	// Abort landing animation setup when:
	// 1) GSAP isn't loaded (prevents runtime errors)
	// 2) User has 'prefers-reduced-motion: reduce' (respect accessibility)
	// 3) Animation already played in this session
	if (
		typeof gsap === "undefined" ||
		window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
		hasPlayedInSession
	) {
		_applyAnimationEndState();
		return;
	}

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					_playAnimation();
					observer.unobserve(entry.target);
				}
			});
		},
		{
			threshold: 0.35,
		}
	);
	observer.observe(container);

	let prevWidth = container.clientWidth;
	let isFirstResize = true;
	const resizeObserver = new ResizeObserver((entries) => {
		for (const entry of entries) {
			const width = entry.contentRect.width;
			if (width !== prevWidth) {
				prevWidth = width;
				if (isFirstResize) {
					isFirstResize = false;
					return;
				}
				utilsDebounce(_applyAnimationEndState)();
			}
		}
	});
	resizeObserver.observe(container);
};

class SpoonAnimation {
	constructor(wrapper) {
		this.wrapper = wrapper;
		this.q = gsap.utils.selector(wrapper);

		this.spoonDown = this.q(".svg-spoon-down");
		this.spoonUp = this.q(".svg-spoon-up");
		this.saltPile = this.q(".svg-salt-pile");
		this.saltSprinkle = this.q(".svg-salt-sprinkle");

		this.createTimelines();
	}

	// Helper method to set elements to the 'empty' starting state
	reset() {
		gsap.set([this.spoonDown, this.saltPile], { y: 0, autoAlpha: 0 });
		gsap.set(this.spoonUp, { y: -0.2, autoAlpha: 1 });
		gsap.set(this.saltSprinkle, { y: -10, autoAlpha: 0 });
	}

	createTimelines() {
		this.masterTL = gsap.timeline({
			paused: true,
			defaults: { ease: "power1.inOut" },
			onStart: () => {
				this.reset();
			},
			onComplete: () => {
				this.reset();
			},
		});

		// --- PART 1: FILLING ANIMATION ---
		this.fillingAnimation = gsap.timeline({
			paused: true,
		});
		this.fillingAnimation
			.set(this.spoonDown, { autoAlpha: 0 }, "enterFrame")
			.to(this.saltSprinkle, { autoAlpha: 1 }, "enterFrame")
			.to(this.saltSprinkle, { y: 0 }, "enterFrame+=0.1")
			.to(this.spoonUp, { y: 0, delay: 0.2, duration: 0.2 }, "enterFrame+=0.1")
			.to(this.saltPile, { autoAlpha: 1, duration: 0.2 }, "enterFrame+=0.3");

		// --- PART 2: EMPTYING ANIMATION ---
		this.emptyingAnimation = gsap.timeline({
			paused: true,
		});
		this.emptyingAnimation
			.set([this.saltSprinkle, this.saltPile, this.spoonUp], {
				y: 0,
				autoAlpha: 1,
			})
			.to(this.saltPile, { autoAlpha: 0 }, "startEmptying")
			.to(
				this.spoonUp,
				{ y: 0, autoAlpha: 0, duration: 0.1 },
				"startEmptying+=0.3"
			)
			.to(this.spoonDown, { autoAlpha: 1, duration: 0.1 }, "startEmptying+=0.3")
			.fromTo(this.spoonDown, { y: 0 }, { y: 5 }, "saltDrop")
			.fromTo(
				this.saltSprinkle,
				{ y: 0, autoAlpha: 1 },
				{ y: 20, autoAlpha: 0 },
				"saltDrop+=0.2"
			)

			.add("endFrame")
			.set(this.spoonDown, { autoAlpha: 0 }, "endFrame")
			.fromTo(
				this.spoonUp,
				{ y: 5, autoAlpha: 1 },
				{ y: 0, autoAlpha: 1, duration: 0.3 },
				"endFrame"
			);

		// Add both timelines to the master sequence
		// Use tweenFromTo for clean control when nesting paused timelines
		this.masterTL
			.add(
				this.fillingAnimation.tweenFromTo(0, this.fillingAnimation.duration())
			)
			.add(
				this.emptyingAnimation.tweenFromTo(
					0,
					this.emptyingAnimation.duration()
				),
				"+=0.3"
			);
	}

	// Plays ONLY the filling sequence (sets status to FULL).
	playFill() {
		this.reset();
		this.fillingAnimation.restart();
	}

	// Plays ONLY the emptying sequence (sets status to EMPTY).
	playEmpty() {
		this.emptyingAnimation.restart();
	}

	// Plays the full cycle (Fill -> Empty).
	playFullCycle() {
		this.masterTL.restart();
	}

	// Dedicate animation to fill spoon with transition
	setFull() {
		this.reset();
		gsap
			.timeline({
				defaults: { ease: "power1.inOut" },
			})
			.set([this.saltSprinkle, this.saltPile, this.spoonUp], { y: 0 })
			.to([this.saltSprinkle, this.saltPile], { autoAlpha: 1 });
	}
}
window.SpoonAnimation = SpoonAnimation;

const initSpoonAnimation = () => {
	document.querySelectorAll(".js-spoon-animation").forEach((wrapper) => {
		const spoonAnim = new window.SpoonAnimation(wrapper);

		if (root.classList.contains("is-cart-empty")) {
			spoonAnim.reset();
		} else {
			spoonAnim.setFull();
		}

		const cartToggle = wrapper.closest(".js-minicart-toggle");
		if (cartToggle) {
			// when the cart is empty and you hover, play the animation once (to completion)
			const isNonCursor =
				vs?.isTouchDevice === true ||
				!window.matchMedia("(hover: hover) and (pointer: fine)").matches;

			let isAnimating = false;
			if (!isNonCursor) {
				// Desktop (fine pointer) – hover to play
				cartToggle.addEventListener("mouseenter", () => {
					if (root.classList.contains("is-cart-empty") && !isAnimating) {
						isAnimating = true;
						spoonAnim.playFullCycle();

						spoonAnim.masterTL.eventCallback("onComplete", () => {
							isAnimating = false;
						});
					}
				});
			}
		}
	});
};

// place global, component, and page level functions here
const playTargetVideo = (
	video,
	externalVideoType,
	player,
	soundOn = false,
	videoHasMuteBtn = false
) => {
	const playVideo = (video) => {
		video.muted = !(videoHasMuteBtn && soundOn);
		video.play();
	};

	const playExternalVideo = (player, externalVideoType) => {
		if (externalVideoType === "vimeo") {
			player.setMuted(!(videoHasMuteBtn && soundOn));
			player.play().catch((error) => {
				if (error.name !== "PlayInterrupted") {
					console.error("Error playing video:", error);
				}
			});
		} else if (externalVideoType === "youtube") {
			// Both functions existing means the YouTube player is fully initialized.
			if (
				typeof player.playVideo == "function" &&
				typeof player.mute === "function"
			) {
				videoHasMuteBtn && soundOn ? player.unMute() : player.mute();
				player.playVideo();
			} else {
				console.warn(
					"YouTube player not fully initialized or methods unavailable."
				);
			}
		}
	};

	if (video) {
		playVideo(video);
	} else if (player) {
		playExternalVideo(player, externalVideoType);
	}
};

const pauseTargetVideo = (video, externalVideoType, player) => {
	const pauseVideo = (video) => {
		video.pause();
		video.muted = true;
	};

	const pauseExternalVideo = (player, externalVideoType) => {
		if (externalVideoType === "vimeo") {
			player.pause().catch((error) => {
				if (error.name !== "PlayInterrupted") {
					console.error("Error pausing video:", error);
				}
			});
			player.setMuted(true);
		} else if (externalVideoType === "youtube") {
			// Both functions existing means the YouTube player is fully initialized.
			if (
				typeof player.pauseVideo === "function" &&
				typeof player.mute === "function"
			) {
				player.pauseVideo();
				player.mute();
			} else {
				console.warn(
					"YouTube player not fully initialized or methods unavailable."
				);
			}
		}
	};

	if (video) {
		pauseVideo(video);
	} else if (player) {
		pauseExternalVideo(player, externalVideoType);
	}
};
const initVideo = () => {
	const handleVideoPlayback = (item, play, targetPlayer) => {
		const videoElement = item.querySelector("video");
		const externalVideo = item.querySelector(".js-external-video");
		const externalVideoType = externalVideo?.dataset.videoHost;
		const videoHasMuteBtn = item.querySelector(
			".js-video-mute.is-active, .js-video-unmute.is-active"
		);
		const soundOn = videoHasMuteBtn?.classList.contains("js-video-mute");
		if (!videoElement && !targetPlayer) {
			return;
		}

		if (play) {
			playTargetVideo(
				videoElement,
				externalVideoType,
				targetPlayer,
				soundOn,
				videoHasMuteBtn
			);
		} else {
			pauseTargetVideo(videoElement, externalVideoType, targetPlayer);
		}
	};

	const handleVideoVisibility = (entry, item) => {
		const isVisible = entry.isIntersecting;
		const videoButtons = item.querySelectorAll(
			".js-video-mute, .js-video-unmute, .js-video-play, .js-video-pause"
		);
		const externalVideo = item.querySelector(".js-external-video");
		const externalVideoId = externalVideo?.dataset.videoId;
		const targetPlayer = allPlayersObj[externalVideoId];

		handleVideoPlayback(item, isVisible, targetPlayer);

		// Reset video buttons and toggle based on visibility
		videoButtons.forEach((button) => button.classList.remove("is-active"));
		if (isVisible) {
			item.querySelector(".js-video-unmute")?.classList.add("is-active");
			item.querySelector(".js-video-pause")?.classList.add("is-active");
		}
	};

	const updateIframeAspectRatio = (item) => {
		const iframe = item.querySelector('iframe[data-video-host="vimeo"]');

		if (!iframe) return;

		const wrapper = iframe.closest(".c-video");
		const player = new Vimeo.Player(iframe);

		Promise.all([player.getVideoWidth(), player.getVideoHeight()]).then(
			function (dimensions) {
				const width = dimensions[0];
				const height = dimensions[1];
				const aspectRatio = (width / height).toFixed(2);
				if (aspectRatio < 1) {
					wrapper.classList.add("is-portrait");
				} else {
					wrapper.classList.add("is-landscape");
				}
				wrapper.style.setProperty("--aspect-ratio", aspectRatio);
			}
		);
	};

	// always play/pause video if section is in view
	document.querySelectorAll(".c-video").forEach((item) => {
		updateIframeAspectRatio(item);

		const observer = new IntersectionObserver(
			([entry]) => handleVideoVisibility(entry, item),
			{ root: null, threshold: [0.4] }
		);
		observer.observe(item);
	});

	const handleVideoControl = (e, action) => {
		const target = e.target.closest(`.js-video-${action}`);
		const videoContainer = target.closest(".c-video");
		const videoElement = videoContainer.querySelector("video");
		const externalVideo = videoContainer.querySelector(".js-external-video");
		const videoHasMuteBtn = videoContainer.querySelector(
			".js-video-mute.is-active, .js-video-unmute.is-active"
		);
		const soundOn = videoHasMuteBtn?.classList.contains("js-video-mute");
		const externalVideoType = externalVideo?.dataset.videoHost;
		const externalVideoId = externalVideo?.dataset.videoId;
		const targetPlayer = allPlayersObj[externalVideoId];

		if (action === "play") {
			playTargetVideo(
				videoElement,
				externalVideoType,
				targetPlayer,
				soundOn,
				videoHasMuteBtn
			);
		} else if (action === "pause") {
			pauseTargetVideo(videoElement, externalVideoType, targetPlayer);
		} else if (action === "mute" || action === "unmute") {
			if (externalVideoType === "youtube") {
				if (typeof targetPlayer.unMute == "function") {
					targetPlayer.isMuted() ? targetPlayer.unMute() : targetPlayer.mute();
				} else {
					console.warn(
						"YouTube player not fully initialized or methods unavailable."
					);
				}
			} else if (externalVideoType === "vimeo") {
				targetPlayer.getMuted().then((muted) => targetPlayer.setMuted(!muted));
			} else if (videoElement) {
				videoElement.muted = action === "mute" ? true : false;
			}
		}

		// Toggle play/pause or mute/unmute button visibility
		if (action === "play" || action === "pause") {
			target.classList.remove("is-active");
			videoContainer
				.querySelector(`.js-video-${action === "play" ? "pause" : "play"}`)
				?.classList.add("is-active");
		} else if (action === "mute" || action === "unmute") {
			videoContainer
				.querySelector(".js-video-mute")
				?.classList.toggle("is-active", action === "unmute");
			videoContainer
				.querySelector(".js-video-unmute")
				?.classList.toggle("is-active", action === "mute");
		}
	};

	on("body", "click", ".js-video-mute", (e) => handleVideoControl(e, "mute"));
	on("body", "click", ".js-video-unmute", (e) =>
		handleVideoControl(e, "unmute")
	);
	on("body", "click", ".js-video-play", (e) => handleVideoControl(e, "play"));
	on("body", "click", ".js-video-pause", (e) => handleVideoControl(e, "pause"));
};

// *** Fetch & replace main content ***
function fetchAndReplace(url, pushState = true) {
	document.documentElement.classList.add("is-transitioning");

	fetch(url)
		.then((response) => response.text())
		.then((html) => {
			const parser = new DOMParser();
			const doc = parser.parseFromString(html, "text/html");

			const newMain = doc.querySelector("main");
			const currentMain = document.querySelector("main");

			if (newMain && currentMain) {
				currentMain.replaceWith(newMain);
			}

			if (pushState) {
				history.pushState({}, "", url);
			}

			// Re-init comboboxes & events
			initComboBox();
			initFrontrowBadge();
			initFrontRowTestimonials();
			initRelatedProduct();

			// Re-init PDP hero if present after swapping product
			initPDP();
			// Re-init yotpo app
			try {
				yotpoWidgetsContainer?.initWidgets();
			} catch (e) {
				console.warn("Yotpo initWidgets failed:", e);
			}

			// Re-init stagger-up animations on swapped content
			ScrollTrigger.getAll().forEach((t) => {
				if (!t.trigger || !document.contains(t.trigger)) t.kill();
			});
			initStaggerUpAnimation();
			ScrollTrigger.refresh();

			setTimeout(() => {
				document.documentElement.classList.remove("is-transitioning");
			}, 50);
		})
		.catch((error) => {
			console.error("Error fetching product:", error);
			document.documentElement.classList.remove("is-transitioning");
		});
}

// *** Bind all combobox events ***
function bindComboEvents(wrapper = document) {
	const comboInputs = wrapper.querySelectorAll(".combobox-input");
	if (!comboInputs.length) return;

	comboInputs.forEach((comboInput) => {
		if (!comboInput.closest(".js-fetch-product")) return;
		comboInput.addEventListener("combobox-change", (e) => {
			fetchAndReplace(e.detail.url);
		});
	});
}

const initCircularSlider = () => {
	const sections = document.querySelectorAll(".js-circular-slider");
	if (!sections.length) return;

	sections.forEach((section) => {
		const sliderEl = section.querySelector(".c-slider");
		const staticCards = section.querySelectorAll(".js-slider-static-card");
		const cards = section.querySelectorAll(".js-slider-card");
		const navButtons = sliderEl?.querySelector(".c-slider__buttons");
		const illustration = sliderEl?.querySelector(
			".js-circular-slider-illustration"
		);
		const circularIndicator = section.querySelector(
			".js-circular-slider-indicator"
		);

		if (!sliderEl || !staticCards.length) return;

		// Wait for embla to initialize
		const embla = sliderEl.embla;
		if (!embla) return;

		const originalSlideCount = cards.length;
		let allCards = [];

		// Clone slides for seamless looping
		const initLoopClones = () => {
			const viewport = sliderEl.querySelector(".embla__viewport");
			const container = sliderEl.querySelector(".embla__container");

			// Remove existing cloned containers
			viewport
				.querySelectorAll(".embla__container.is-clone")
				.forEach((clone) => {
					clone.remove();
				});

			// Clone the entire container (before and after)
			const cloneBefore = container.cloneNode(true);
			const cloneAfter = container.cloneNode(true);

			cloneBefore.classList.add("is-clone", "is-clone-before");
			cloneAfter.classList.add("is-clone", "is-clone-after");

			// Position clones to create seamless loop
			cloneBefore.style.left = originalSlideCount * -100 + "%";
			cloneAfter.style.left = originalSlideCount * 100 + "%";

			// Insert clones
			viewport.appendChild(cloneBefore);
			viewport.appendChild(cloneAfter);

			// Update allCards to include all visible cards (original + clones)
			allCards = [
				...Array.from(cloneBefore.querySelectorAll(".js-slider-card")),
				...Array.from(container.querySelectorAll(".js-slider-card")),
				...Array.from(cloneAfter.querySelectorAll(".js-slider-card")),
			];
		};

		// Setup image
		const setupImages = () => {
			allCards.forEach((card, idx) => {
				const imgWrapper = card.querySelector(
					".js-circular-slider-illustration"
				);

				// Map to original static card using modulo
				const originalIndex = idx % originalSlideCount;
				const refCard = staticCards[originalIndex]?.firstElementChild;
				if (!imgWrapper || !refCard) return;

				// Calculate gap between children
				const children = Array.from(refCard.children);
				if (children.length < 2) return;

				// Get positions of first two children to calculate gap
				const firstChild = children[0];
				const secondChild = children[1];
				const firstChildRect = firstChild.getBoundingClientRect();
				const secondChildRect = secondChild.getBoundingClientRect();

				const gap = secondChildRect.top - firstChildRect.bottom;
				// Set image height to 90% of gap
				const imageHeight = gap * 0.9;
				imgWrapper.style.height = `${imageHeight}px`;
			});
		};

		// Setup circular indicators
		const setupCircularIndicators = () => {
			if (!circularIndicator) return;

			const indicators = circularIndicator.querySelectorAll("span");
			const totalSlides = indicators.length;
			const rawRadius =
				parseFloat(
					getComputedStyle(circularIndicator).getPropertyValue(
						"--circular-radius"
					)
				) || 350;
			const indicatorSize =
				parseFloat(
					getComputedStyle(circularIndicator).getPropertyValue(
						"--adjusted-radius"
					)
				) || 80;
			const radius = rawRadius - indicatorSize / 2; // Adjust radius to account for indicator size

			// Position each indicator in a circle
			indicators.forEach((span, i) => {
				const angle = (360 / totalSlides) * i - 90; // Start from top (270deg)
				const radians = (angle * Math.PI) / 180;
				// Calculate position using polar coordinates
				const x = radius * Math.cos(radians);
				const y = radius * Math.sin(radians);

				const rotation = angle - 90;
				span.style.transform = `translate(${x}px, ${y}px) rotate(${rotation}deg)`;
			});
		};

		// Rotate to active indicator
		const rotateToIndicator = (index) => {
			if (!circularIndicator) return;

			const indicators = circularIndicator.querySelectorAll("span");
			const totalSlides = indicators.length;
			const anglePerSlide = 360 / totalSlides;
			const targetAngle = -(anglePerSlide * index);

			circularIndicator.style.transform = `rotate(${targetAngle}deg)`;
		};

		// CONTINUOUS update: animate cards based on scroll progress instead of only on snap
		const updateCircularCards = () => {
			const snaps = embla.scrollSnapList(); // normalized [0..1]
			const progress = embla.scrollProgress(); // 0..1
			const n = snaps.length || 1;

			// Get cloned containers
			const viewport = sliderEl.querySelector(".embla__viewport");
			const container = sliderEl.querySelector(".embla__container");
			const cloneBefore = viewport.querySelector(".is-clone-before");
			const cloneAfter = viewport.querySelector(".is-clone-after");

			// Move cloned containers based on scroll progress
			if (cloneBefore && cloneAfter) {
				// Calculate how much the original container has moved
				const containerTransform = getComputedStyle(container).transform;
				const matrix = new DOMMatrix(containerTransform);
				const currentX = matrix.m41; // translateX value

				// Apply same transform to clones
				cloneBefore.style.transform = `translateX(${currentX}px)`;
				cloneAfter.style.transform = `translateX(${currentX}px)`;
			}

			allCards.forEach((card, i) => {
				const slideIndex = i % originalSlideCount;
				let diffToTarget = snaps[slideIndex] - progress;

				// Wrap to nearest when loop is enabled so diff stays in [-0.5, 0.5]
				if (diffToTarget > 0.5) diffToTarget -= 1;
				else if (diffToTarget < -0.5) diffToTarget += 1;

				const dist = Math.abs(diffToTarget) * n; // continuous distance from center
				const baseOffset = 30;
				const yOffset = baseOffset * Math.pow(dist, 2.5);
				const scale = Math.max(1 - dist * 0.2, 0.6);
				const opacity = Math.max(1 - dist * 0.4, 0);

				card.style.transform = `translateY(${yOffset}px) scale(${scale})`;
				card.style.opacity = `${opacity}`;
			});
		};

		// Function to update nav button position based on illustration height
		const updateNavButtonPosition = () => {
			if (!navButtons || !illustration) return;

			const illustrationHeight = illustration.offsetHeight;
			const halfHeight = illustrationHeight / 2;

			// Add half illustration height to current top
			navButtons.style.top = `calc(var(--slider-padding) + var(--content-offset) +
					var(--illustration-offset) + ${halfHeight}px)`;
		};

		// Function to update active static card
		const updateActiveCard = (index) => {
			staticCards.forEach((card, i) => {
				if (i === index) {
					card.classList.add("is-active");
					card.setAttribute("aria-hidden", "false");
				} else {
					card.classList.remove("is-active");
					card.setAttribute("aria-hidden", "true");
				}
			});
			// Rotate circular indicators
			rotateToIndicator(index);
		};

		// Listen to slide changes
		if (sliderEl.onSelectSlide) {
			sliderEl.onSelectSlide((index) => {
				updateActiveCard(index);
			});
		} else {
			embla.on("select", () => {
				const selectedIndex = embla.selectedScrollSnap();
				updateActiveCard(selectedIndex);
			});
		}

		// Continuous animation on drag/scroll and on reInit
		embla.on("scroll", updateCircularCards);

		initLoopClones();

		setupImages();
		// Initialize circular indicators
		setupCircularIndicators();
		updateCircularCards();

		// Set initial active card
		updateActiveCard(embla.selectedScrollSnap());

		// Initial nav button position
		updateNavButtonPosition();

		// Update nav button position on window resize
		// Update on window resize
		window.addEventListener("resize", () => {
			setupImages();
			setupCircularIndicators();
			updateNavButtonPosition();
			rotateToIndicator(embla.selectedScrollSnap());
		});
	});
};

const initPurchaseSlider = () => {
	const sections = document.querySelectorAll(".js-purchase-slider");
	if (!sections.length) return;

	sections.forEach((section) => {
		const sliderEl = section.querySelector(".js-purchase-slider__slider");
		const navItems = section.querySelectorAll(".js-purchase-slider__nav-item");
		const ctaWrap = section.querySelector(".js-purchase-slider__ctas");
		const decoWrapper = section.querySelector(
			".js-purchase-slider__decorations"
		);
		let decoTimeline = null;
		let badgeTimeline = null;
		let ctaTimeline = null;

		if (!sliderEl || !ctaWrap || !sliderEl.embla) return;

		const embla = sliderEl.embla;
		// Pre-rendered CTA items
		const ctaItems = ctaWrap.querySelectorAll(".js-purchase-slider__cta");

		section.addEventListener("mousemove", (e) => {
			initParallaxAnimation(e, section, { duration: 1, stagger: 0.01 });
		});

		function handleLinkFocus(el) {
			el?.parentNode?.classList.add("is-active");
		}

		function handleLinkBlur(el) {
			setTimeout(() => {
				if (!el.parentNode.contains(document.activeElement)) {
					el.parentNode.classList.remove("is-active");
				}
			}, 0);
		}
		function setActiveNav(index) {
			navItems.forEach((li) => {
				const inner = li.querySelector(".js-purchase-slider__nav-item-inner");
				if (inner) {
					const isActive = parseInt(li.dataset.index) === index;
					const link = li.querySelector(".js-purchase-slider__nav-link");
					if (isActive) {
						inner.classList.add("is-active");
						link?.removeAttribute("aria-hidden");
						link?.setAttribute("tabindex", "0");

						// Create stable references if they don't exist
						if (!li._focusHandlers) {
							const onFocus = () => handleLinkFocus(link);
							const onBlur = () => handleLinkBlur(link);
							li._focusHandlers = { onFocus, onBlur };
						}

						const { onFocus, onBlur } = li._focusHandlers;

						if (!li._listenersAttached) {
							link?.addEventListener("focus", onFocus);
							link?.addEventListener("blur", onBlur);
							li._listenersAttached = true;
						}
					} else {
						inner.classList.remove("is-active");
						link?.setAttribute("aria-hidden", "true");
						link?.setAttribute("tabindex", "-1");

						if (li._listenersAttached && li._focusHandlers) {
							const { onFocus, onBlur } = li._focusHandlers;
							link?.removeEventListener("focus", onFocus);
							link?.removeEventListener("blur", onBlur);
							li._listenersAttached = false;
						}
					}
				}
			});
		}
		// Animate content CTA block change
		function updateCTA(productHandle) {
			const current = ctaWrap.querySelector(
				".js-purchase-slider__cta.is-active"
			);
			const next = Array.from(ctaItems).find(
				(item) => item.dataset.productHandle === productHandle
			);
			if (!next || current === next) return;
			const hasGSAP = typeof gsap !== "undefined";
			if (!hasGSAP) {
				ctaItems.forEach((item) => {
					if (item === next) {
						item.classList.add("is-active");
					} else {
						item.classList.remove("is-active");
					}
				});
				return;
			}

			if (ctaTimeline) {
				ctaTimeline.kill();
			}
			ctaTimeline = gsap.timeline();

			// Fade out current
			if (current) {
				ctaTimeline.to(current, {
					autoAlpha: 0,
					duration: 0.3,
					onComplete: () => {
						current.classList.remove("is-active");

						current.style.removeProperty("opacity");
						current.style.removeProperty("transform");
					},
				});
			}

			// Reveal next fade-in
			ctaTimeline.set(next, { autoAlpha: 0, y: 8 });

			ctaTimeline.to(next, {
				autoAlpha: 1,
				y: 0,
				duration: 0.3,
				ease: "power2.out",
				onStart: () => next.classList.add("is-active"),
				clearProps: "all",
			});
		}

		function activate(index) {
			setActiveNav(index);
			const target = section.querySelector(
				`.js-purchase-slider__nav-item[data-index="${index}"]`
			);
			const productHandle = target?.dataset.handle;
			updateCTA(productHandle);

			renderDecorations(productHandle);

			// Prefetch next/prev on idle
			if ("requestIdleCallback" in window) {
				const nextIdx = index + 1 < navItems.length ? index + 1 : null;
				const prevIdx = index - 1 >= 0 ? index - 1 : null;
				requestIdleCallback(() => {
					if (nextIdx !== null) {
						const h = section.querySelector(
							`.js-purchase-slider__nav-item[data-index="${nextIdx}"]`
						)?.dataset.handle;
						h && prefetchDecorations(h);
					}
					if (prevIdx !== null) {
						const h = section.querySelector(
							`.js-purchase-slider__nav-item[data-index="${prevIdx}"]`
						)?.dataset.handle;
						h && prefetchDecorations(h);
					}
				});
			}
			renderValueBadges(productHandle);
		}

		// Render decorations for handle from its template
		function renderDecorations(handle) {
			if (!decoWrapper) return;
			const tpl = section.querySelector(`#tpl-purchase-decorations-${handle}`);

			// Kill previous timeline
			if (decoTimeline) decoTimeline.kill();
			decoTimeline = null;

			// Guard: no GSAP or reduced motion
			if (
				typeof gsap === "undefined" ||
				window.matchMedia("(prefers-reduced-motion: reduce)").matches
			) {
				decoWrapper.innerHTML = tpl ? tpl.innerHTML : "";
				return;
			}

			const fadeInDuration = 0.8;
			decoTimeline = gsap.timeline({
				defaults: { duration: fadeInDuration, ease: "power2.out" },
			});

			// Phase 1: Fade out container (if any decorations exist)
			const fadeOutDuration = 0.15;
			decoTimeline.to(decoWrapper, {
				autoAlpha: 0,
				duration: fadeOutDuration,
				ease: "power2.out",
			});

			// Phase 2: Swap HTML content
			decoTimeline.call(
				() => {
					decoWrapper.innerHTML = tpl ? tpl.innerHTML : "";

					// Get new decorations after swap
					const newLeftDecos = Array.from(
						decoWrapper.querySelectorAll('[data-side="left"]')
					);

					const newRightDecos = Array.from(
						decoWrapper.querySelectorAll('[data-side="right"]')
					);

					if (!newLeftDecos.length && !newRightDecos.length) return;

					// Prep initial state for new images (fully transparent)
					gsap.set(newLeftDecos, {
						x: "-5%",
						y: "5%",
						willChange: "transform, opacity",
					});
					gsap.set(newRightDecos, {
						x: "5%",
						y: "5%",
						willChange: "transform, opacity",
					});

					// Wait for all images to load before animating
					const allImages = [
						...newLeftDecos.flatMap((deco) =>
							Array.from(deco.querySelectorAll("img"))
						),
						...newRightDecos.flatMap((deco) =>
							Array.from(deco.querySelectorAll("img"))
						),
					];

					// Create array of load promises
					const imageLoadPromises = allImages.map((img) => {
						return new Promise((resolve) => {
							if (img.complete) {
								resolve();
							} else {
								img.addEventListener("load", resolve, { once: true });
								img.addEventListener("error", resolve, { once: true }); // resolve on error too
							}
						});
					});

					// Wait for all images to load, then animate
					Promise.all(imageLoadPromises).then(() => {
						// Phase 3: Fade in the container and new decorations with stagger
						decoTimeline.to(
							decoWrapper,
							{
								autoAlpha: 1,
								duration: fadeInDuration,
							},
							"+=0.2"
						);

						decoTimeline.to(
							newLeftDecos,
							{
								x: "0%",
								y: "0%",
								clearProps: "will-change",
							},
							"<"
						);

						decoTimeline.to(
							newRightDecos,
							{
								x: "0%",
								y: "0%",
								clearProps: "will-change",
							},
							"<"
						);
					});
				},
				null,
				`+=${fadeOutDuration}`
			);
		}

		function prefetchDecorations(handle) {
			const tpl = section.querySelector(`#tpl-purchase-decorations-${handle}`);
			if (!tpl) return;
			const tmp = document.createElement("div");
			tmp.innerHTML = tpl.innerHTML;
			tmp.querySelectorAll("img[srcset]").forEach((img) => {
				const link = document.createElement("link");
				link.rel = "prefetch";
				link.as = "image";
				link.href = img.currentSrc || img.src;
				document.head.appendChild(link);
			});
		}
		function renderValueBadges(handle) {
			const tpl = section.querySelector(`#tpl-purchase-value-badge-${handle}`);
			const badgeWrap = section.querySelector(
				".js-purchase-slider__value-badges"
			);
			if (tpl && badgeWrap) {
				badgeWrap.innerHTML = tpl.innerHTML;
			} else if (badgeWrap) {
				badgeWrap.innerHTML = "";
			}

			// Kill previous badge timeline
			if (badgeTimeline) {
				badgeTimeline.kill();
				badgeTimeline = null;
			}

			// Guard: no GSAP or reduced motion
			if (
				typeof gsap === "undefined" ||
				window.matchMedia("(prefers-reduced-motion: reduce)").matches
			) {
				return;
			}
			// Animate in new badges
			if (badgeWrap && badgeWrap.children.length) {
				const badges = Array.from(badgeWrap.children);
				gsap.set(badges, {
					opacity: 0,
					y: "10%",
					scale: 0.6,
					willChange: "transform, opacity",
				});
				badgeTimeline = gsap.timeline({
					defaults: { duration: 0.3, ease: "power2.out" },
					onComplete: () => {
						badges.forEach((b) => b.style.removeProperty("will-change"));
					},
				});
				badgeTimeline.to(badges, {
					opacity: 1,
					y: 0,
					scale: 1,
					stagger: 0.08,
					clearProps: "transform,opacity",
				});
			}
		}

		// Embla slide change (swipe, next/prev)
		if (sliderEl?.onSelectSlide) {
			sliderEl.onSelectSlide(activate);
		} else {
			embla.on("select", () => {
				activate(embla.selectedScrollSnap());
			});
		}

		// Nav item click => scroll slider & activate
		navItems.forEach((li) => {
			li.addEventListener("click", (e) => {
				const idx = parseInt(li.dataset.index, 10);
				// Do nothing if clicking the currently selected item
				if (idx === embla.selectedScrollSnap()) return;
				embla.scrollTo(idx);
				// embla 'select' event will handle activate(), but we can optimistically update:
				activate(idx);
			});
		});

		// Initial (ensure consistency if first slide not 0 later)
		activate(embla.selectedScrollSnap());
	});
};

const initPurchaseExplorer = () => {
	const toggleActiveProduct = (wrapper, productId) => {
		wrapper.querySelectorAll(".is-active").forEach((el) => {
			el.classList.remove("is-active");
			el.setAttribute("aria-hidden", true);
		});
		wrapper.querySelectorAll(`[data-product="${productId}"]`).forEach((el) => {
			el.classList.add("is-active");
			el.setAttribute("aria-hidden", false);
		});
	};

	on("body", "click", ".js-explorer-trigger", (e) => {
		const trigger = e.target.closest(".js-explorer-trigger");
		const wrapper = e.target.closest(".js-explorer-wrapper");

		const productId = trigger.dataset.product;
		toggleActiveProduct(wrapper, productId);
	});

	// Update CTA URL on slide change
	document.querySelectorAll(".js-explorer-wrapper").forEach((wrapper) => {
		const slides = wrapper.querySelectorAll('.embla__slide > [name="id"]');
		const addBtn = wrapper.querySelector(".js-explorer-purchase-btn");

		const ctaBtn = wrapper.querySelector(".js-explorer-url");
		const embla = wrapper.querySelector(".embla");

		if (!embla || !ctaBtn || !slides.length) return;

		embla.onSelectSlide((index) => {
			const targetSlide = slides[index];
			const productUrl = targetSlide.dataset.productUrl;
			if (productUrl) ctaBtn.setAttribute("href", productUrl);

			const available = targetSlide.dataset.productAvailability === "true";
			addBtn.disabled = !available;
		});
	});

	// Add product to cart
	on("body", "click", ".js-explorer-purchase-btn", (e) => {
		const wrapper = e.target.closest(".js-explorer-wrapper");
		if (!wrapper) return;
		const targetSlide = wrapper.querySelector(".embla__slide.is-snapped");
		const item = cCart.getItemData(targetSlide);
		cCart.addItems({
			items: [item],
			callback: () => {
				loader.complete(root);
				cCart.minicartOpen();
			},
		});
	});
};

const initBlog = () => {
	if (window.location.hash === "#blogContent") {
		const blogContent = document.querySelector("#blogContent");
		if (blogContent) {
			setTimeout(() => {
				blogContent.scrollIntoView({ behavior: "smooth", block: "start" });
			}, 400);
		}
	}
};

const initWholesalePage = () => {
	const containers = document.querySelectorAll(".js-wholesale-content");
	if (!containers.length) return;

	containers.forEach((container) => {
		const triggers = container.querySelectorAll(
			".js-wholesale-products-trigger"
		);
		if (!triggers.length) return;

		// Activate each set's images and leaves independently as it scrolls into view
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						entry.target
							.querySelectorAll(
								".js-wholesale-product-image, .js-wholesale-products-decoration"
							)
							.forEach((el) => el.classList.add("is-active"));
						observer.unobserve(entry.target);
					}
				});
			},
			{
				threshold: 0.5,
			}
		);
		triggers.forEach((trigger) => observer.observe(trigger));
	});
};

const initShippingAndReturnsPage = () => {
	const container = document.querySelector(".js-shipping-and-returns-content");
	if (!container) return;

	// --- Nav click handlers ---
	const navLinks = container.querySelectorAll(
		".js-shipping-and-returns-content-link"
	);
	const navButtons = container.querySelectorAll(
		".js-shipping-and-returns-content-btn"
	);
	const sectionGroups = container.querySelectorAll(
		".js-shipping-and-returns-content-policy"
	); // your content sections
	let ignoreScrollUpdate = false;
	const observers = [];

	// Helper: set active nav item
	const setActiveNavItem = (handle, alsoSetBtns = true) => {
		navLinks.forEach((item) => item.classList.remove("is-active"));

		const matchNav = Array.from(navLinks).find(
			(item) => item.dataset.handle === handle
		);
		matchNav?.classList.add("is-active");
		if (alsoSetBtns) {
			navButtons.forEach((item) => item.classList.remove("is-active"));
			const matchBtn = Array.from(navButtons).find(
				(item) => item.dataset.handle === handle
			);
			matchBtn?.classList.add("is-active");
		}
	};
	// Set active visible section (for mobile, etc.)
	const setActiveGroup = (handle) => {
		sectionGroups.forEach((group) => group.classList.remove("is-active"));
		const matching = Array.from(sectionGroups).find(
			(item) => item.id === handle
		);
		matching?.classList.add("is-active");
	};

	navLinks.forEach((navLink) => {
		navLink.addEventListener("click", () => {
			const handle = navLink.dataset.handle;
			setActiveNavItem(handle);

			ignoreScrollUpdate = true;
			setTimeout(() => {
				ignoreScrollUpdate = false;
			}, 1000);
		});
	});
	navButtons.forEach((navButton) => {
		navButton.addEventListener("click", () => {
			const handle = navButton.dataset.handle;
			setActiveNavItem(handle);
			setActiveGroup(handle);
		});
	});

	// --- IntersectionObserver for scroll navigation ---
	sectionGroups.forEach((section) => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (ignoreScrollUpdate) return;
				if (entry.isIntersecting) {
					setActiveNavItem(section.id, false);
				}
			},
			{
				rootMargin: "-20% 0px -80% 0px",
				threshold: 0,
			}
		);
		observer.observe(section);
		observers.push(observer);
	});
};

const initPLP = () => {
	on("body", "click", ".js-filter-type-toggle", (e) => {
		const target = e.target.closest(".js-filter-type-toggle");
		const targetValue = target.dataset.value;

		const filter = document.querySelector(
			`[name="filter.p.product_type"][value="${targetValue}"]`
		);
		target.classList.toggle("is-active");
		filter?.click();
	});
};

const initRelatedProduct = () => {
	const handleIntersection = (entries, observer) => {
		if (!entries[0].isIntersecting) return;

		observer.unobserve(relatedProductsSection);

		const url = relatedProductsSection.dataset.url;

		fetch(url)
			.then((response) => response.text())
			.then((text) => {
				const html = document.createElement("div");
				html.innerHTML = text;
				const relatedProducts = html.querySelector(".js-product-related");

				if (relatedProducts && relatedProducts.innerHTML.trim().length) {
					relatedProductsSection.innerHTML = relatedProducts.innerHTML;
				}
			})
			.catch((e) => {
				console.error(e);
			});
	};

	const relatedProductsSection = document.querySelector(".js-product-related");
	if (!relatedProductsSection) return;
	const observer = new IntersectionObserver(handleIntersection, {
		rootMargin: "0px 0px 200px 0px",
	});

	observer.observe(relatedProductsSection);
};

const initPDP = () => {
	const productHero = document.querySelector(".product-hero");
	if (productHero) {
		bindComboEvents();

		productHero.addEventListener("mousemove", (e) => {
			initParallaxAnimation(e, productHero);
		});

		// variant specific gallery
		const galleries = productHero.querySelectorAll(".js-slider");

		if (galleries.length > 1) {
			// Map variant handles to their gallery elements
			const galleryMap = new Map();
			galleries.forEach((gallery) => {
				const key = gallery.dataset.slider;
				if (key) galleryMap.set(key, gallery);
			});

			on(
				"body",
				"change",
				".c-product-form .js-variant-slider-trigger",
				(e) => {
					const target = e.target.closest(".js-variant-slider-trigger");
					const targetValue = formatHandleize(target.value);

					// Deactivate all galleries
					galleries.forEach((gallery) => {
						gallery.classList.remove("is-active");
						gallery.setAttribute("aria-hidden", "true");
					});

					// Activate the matching gallery (fast lookup)
					const matchedGallery = galleryMap.get(targetValue);
					if (matchedGallery) {
						matchedGallery.classList.add("is-active");
						matchedGallery.setAttribute("aria-hidden", "false");
					} else {
						console.warn(
							`No matching gallery found for variant: ${targetValue}`
						);
					}
				}
			);
		}
	}
};

// *** Handle back/forward navigation in PDP when .js-fetch-product is present ***
window.addEventListener("popstate", () => {
	if (!document.querySelector(".js-fetch-product")) return;
	fetchAndReplace(location.href, false);
});

const initEnvelopeAnimation = () => {
	const envelope = document.querySelector(".js-envelope");
	if (!envelope || !gsap || !ScrollTrigger) return;

	const chapters = document.querySelector(".js-chapter");
	if (!chapters) return; // Envelope needs chapters for timeline mode

	const envelopeTop = envelope.querySelector(".js-envelope-top");
	const envelopeBottom = envelope.querySelector(".js-envelope-bottom");

	// Wait for all envelope images to load before showing
	const envelopeImages = envelope.querySelectorAll("img");
	const imageLoadPromises = Array.from(envelopeImages).map((img) => {
		return new Promise((resolve) => {
			if (img.complete) {
				resolve();
			} else {
				img.addEventListener("load", resolve, { once: true });
				img.addEventListener("error", resolve, { once: true }); // resolve on error too
			}
		});
	});

	// Show envelope once all images are loaded
	Promise.all(imageLoadPromises).then(() => {
		envelope.classList.add("is-loaded");
	});

	// Create envelope opening timeline
	const envelopeTimeline = gsap.timeline({
		scrollTrigger: {
			trigger: envelope,
			start: "top top",
			end: `+=${window.innerHeight * 0.5}`, // 50vh of scroll (match start of next animation)
			scrub: 1,
			invalidateOnRefresh: true, // Recalculate transforms on resize
		},
		defaults: {
			duration: 2,
			ease: "power1.inOut",
		},
	});
	if (envelopeTop) envelopeTimeline.to(envelopeTop, { yPercent: -100 });
	if (envelopeBottom)
		envelopeTimeline.to(envelopeBottom, { yPercent: 100 }, "<");
	envelopeTimeline.to(envelope, {
		autoAlpha: 0,
		duration: 0.1,
	});
	envelopeTimeline.to({}, { duration: 3 });

	on("body", "click", ".js-envelope-trigger", (e) => {
		// Get the ScrollTrigger instance
		const scrollTrigger = envelopeTimeline.scrollTrigger;
		// Calculate target scroll position (end of envelope timeline)
		const targetScroll = scrollTrigger.end;

		// Scroll to the end of envelope timeline using native smooth scroll
		gsap.to(window, {
			scrollTo: { y: targetScroll * 0.66, autoKill: true },
			duration: 2,
			ease: "power1.inOut",
		});
	});
};

const initChapterAnimation = () => {
	const chaptersContainer = document.querySelector(".js-chapter");
	const sections = chaptersContainer.querySelectorAll(".js-chapter-section");
	if (sections.length === 0) return;
	chaptersContainer.style.setProperty("--section-count", sections.length);

	// Get spinner elements
	const spinWrapper = chaptersContainer.querySelector(".js-ani-transform");
	const spinEls = chaptersContainer.querySelectorAll(".js-ani-spin");

	// Animations
	const handleParallax = (e) => {
		const section = e.currentTarget;
		initParallaxAnimation(e, section, { duration: 2, stagger: 0.02 });
	};

	// Scroll Based Animation
	// 1. Create one Main Timeline
	const chaptersTimeline = gsap.timeline();

	chaptersTimeline.addLabel("start");

	sections.forEach((section, index) => {
		const q = gsap.utils.selector(section);
		const defaultDuration = 1;
		const localTl = gsap.timeline({
			defaults: { duration: defaultDuration, ease: "none" },
		});

		// --- Enter Phase (1s) ---
		// Add section fade animations
		localTl
			.fromTo(q(".js-ani-fade"), { autoAlpha: 0 }, { autoAlpha: 1 })
			.fromTo(
				q(".js-ani-rotateFade"),
				{
					x: "50vw",
					y: "10vh",
					scale: 0.85,
					rotate: 10,
					autoAlpha: 0,
				},
				{
					x: 0,
					y: 0,
					scale: 1,
					rotate: 0,
					autoAlpha: 1,
				},
				"<"
			);

		// Animate spinner position based on section index
		// Each section moves spinner by 25%: 0%, 25%, 50%, 75% for sections 0-3
		const spinnerPercent = index * 25;
		localTl.to(
			spinWrapper,
			{
				xPercent: -spinnerPercent,
			},
			"<"
		);

		// Animate individual spinner elements
		spinEls.forEach((el) => {
			const chapter = parseInt(el.dataset.chapter, 10);
			const chapterIndex = chapter - 1;
			const distance = Math.abs(chapterIndex - index);
			const yOffset = `${distance * 5}vh`; // 5vh per distance unit (responsive to viewport)

			// For the first section, set positions instantly; for others, animate
			if (index === 0) {
				localTl.set(el, { y: yOffset }, "<");
			} else {
				localTl.to(el, { y: yOffset }, "<");
			}
		});

		// --- Hold State (0.2s) ---
		// Pause with section fully visible before exit phase
		localTl.to({}, { duration: 0.5 });

		// --- Exit Phase (1s) ---
		// (Except for the last section, which stays visible)
		if (index !== sections.length - 1) {
			localTl.to(q(".js-ani-fade"), { autoAlpha: 0 }).to(
				q(".js-ani-rotateFade"),
				{
					x: "-50vw",
					y: "10vh",
					scale: 0.85,
					rotate: -10,
					autoAlpha: 0,
				},
				"<"
			);
		}

		// --- Add to Main Timeline ---
		// Sequential Logic:
		// Sections play one after another with no overlap.
		// This ensures one section fades out completely before the next starts entering.
		const position = index === 0 ? 0 : ">";
		chaptersTimeline.add(localTl, position);

		// --- Add Label ---
		chaptersTimeline.addLabel(
			`section-${index}-active`,
			index === 0 ? defaultDuration : `<+${defaultDuration}`
		);
	});

	// 2. Create ONE Single ScrollTrigger to control the Main Timeline
	const mainST = ScrollTrigger.create({
		animation: chaptersTimeline,
		trigger: chaptersContainer,
		start: "top top",
		end: "bottom bottom",
		scrub: 1,
		onUpdate: (self) => {
			// Calculate which section is currently "active" based on progress
			const totalProgress = self.progress;
			const sectionIndex = Math.min(
				Math.floor(totalProgress * sections.length),
				sections.length - 1
			);

			// Toggle classes based on the calculated index
			sections.forEach((section, i) => {
				if (i === sectionIndex) {
					section.setAttribute("aria-hidden", "false");
					// Add parallax listener
					section.addEventListener("mousemove", handleParallax);
				} else {
					section.setAttribute("aria-hidden", "true");
					// Remove parallax listener
					section.removeEventListener("mousemove", handleParallax);
				}
			});
		},

		// 3. The Snap Logic
		snap: {
			snapTo: "labelsDirectional", // Snaps to the 'section-X-active' labels we added
			duration: 1.5, // Slower duration for an ambient feel
			delay: 0.1, // Slight delay to let the movement drift
			ease: "power1.inOut", // Smooth sine curve mimicking a wave
		},
	});

	// Interactive Based Animation
	let isAnimating = false;
	on("body", "click", ".js-chapter-trigger", (e) => {
		if (isAnimating) return; // prevent multiple clicks
		const trigger = e.target.closest(".js-chapter-trigger");
		const targetChapter = parseInt(trigger.dataset.chapter, 10);
		const targetIndex = targetChapter - 1; // Convert 1-based to 0-based

		// Find the label for this section
		const label = `section-${targetIndex}-active`;

		// Get the scroll position for this label from the ScrollTrigger
		const scrollPos = mainST.labelToScroll(label);

		if (scrollPos !== null) {
			isAnimating = true;
			gsap.to(window, {
				scrollTo: scrollPos,
				duration: 1.5,
				ease: "power1.inOut",
				onComplete: () => {
					isAnimating = false;
				},
			});
		}
	});
};
// Parallax effect
function initParallaxAnimation(
	e,
	wrapper,
	settings = { duration: 2, stagger: 0.1 }
) {
	// Don't run if wrapper is currently transitioning
	if (wrapper.classList.contains("is-transitioning")) return;

	const rect = wrapper.getBoundingClientRect();
	const relX = e.clientX - rect.left;
	const relY = e.clientY - rect.top;

	const targets = wrapper.querySelectorAll(".js-ani-parallax");

	if (!targets.length) return;

	const moves = Array.from(targets).map((el) => {
		const movement = parseFloat(el.dataset.movement) || 30;
		return {
			x: ((relX - rect.width / 2) / rect.width) * movement,
			y: ((relY - rect.height / 2) / rect.height) * movement,
		};
	});

	gsap.to(targets, {
		duration: settings.duration,
		x: (i) => moves[i].x,
		y: (i) => moves[i].y,
		ease: "power2.out",
		stagger: settings.stagger,
	});
}

const initStaggerUpAnimation = () => {
	if (!gsap || window.matchMedia("(prefers-reduced-motion: reduce)").matches)
		return;

	const offset =
		parseInt(
			getComputedStyle(document.documentElement).getPropertyValue(
				"--ani-stagger-up-offset"
			)
		) | 20;

	// Initial state
	gsap.set(".js-ani-stagger-up", { autoAlpha: 0, y: offset });

	ScrollTrigger.batch(".js-ani-stagger-up", {
		onEnter: (elements) => {
			elements.forEach((el, i) => {
				const duration = parseFloat(el.dataset.duration) || 0.8; // use data-duration if exists
				gsap.to(el, {
					autoAlpha: 1,
					y: 0,
					duration: duration,
					ease: "power2.out",
					delay: i * 0.2,
				});
			});
		},
		start: "top 85%",
		once: true,
		// markers: true,
	});
};

// Re-init stagger-up animations after filter/sort swaps content (js-shopify.js)
document.addEventListener("filters:updated", () => {
	if (typeof ScrollTrigger === "undefined") return;
	ScrollTrigger.getAll().forEach((t) => {
		if (!t.trigger || !document.contains(t.trigger)) t.kill();
	});
	initStaggerUpAnimation();
	ScrollTrigger.refresh();
});

// Load Vimeo / Youtube players
let allPlayersObj = {};
// youtube fix - lace this event at global level
window.onYouTubeIframeAPIReady = () => {
	document
		.querySelectorAll('.js-external-video[data-video-host="youtube"]')
		.forEach((video) => {
			const externalVideoId = video.dataset.videoId;
			const player = new YT.Player(video, {
				videoId: externalVideoId,
				playerVars: { playsinline: 1 },
			});
			allPlayersObj[externalVideoId] = player;
		});
};

window.addEventListener("load", () => {
	document
		.querySelectorAll('.js-external-video[data-video-host="vimeo"]')
		.forEach((video) => {
			const externalVideoId = video.dataset.videoId;
			const player = new Vimeo.Player(video);
			player.on("loaded", () => (allPlayersObj[externalVideoId] = player));
		});

	setTimeout(() => {
		// set a slight delay for player to load completely
		initVideo();
	}, 400);
});

// Safely run a function and catch errors
function safeRun(fn) {
	try {
		fn?.();
	} catch (error) {
		console.error(`Init error in ${fn.name || "anonymous function"}:`, error);
	}
}

const initFooter = () => {
	const footer = document.querySelector(".js-footer");
	const postcard = footer.querySelector(".js-footer-postcard");

	// custom hubspot hooks
	on("body", "click", ".js-chat-open", () => {
		if (window.HubSpotConversations) {
			root.classList.add("is-chat-open");
			window.HubSpotConversations?.widget.open();
		}
	});

	window.HubSpotConversations?.on("widgetClosed", () => {
		root.classList.remove("is-chat-open");
	});

	// Postcard animation with IntersectionObserver
	if (window.gsap && postcard && !vs.isMobileDown) {
		// Set initial state - rotating slightly from 5 degrees (approx "5%")
		gsap.set(postcard, {
			y: vs.isTabletDown ? "10%" : "20%",
			rotation: 2,
		});

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						gsap.to(postcard, {
							y: "0%",
							rotation: 0,
							duration: 1.2,
							ease: "power3.out",
						});
						observer.unobserve(entry.target);
					}
				});
			},
			{
				threshold: 0.1, // Trigger when at least 10% is visible
			}
		);

		observer.observe(postcard);
	}
};

document.addEventListener("DOMContentLoaded", () => {
	// Run common initializations
	[
		// initVideo,
		initGSAP,
		initHeader,
		initLanding,
		initCircularSlider,
		initSpoonAnimation,
		initComboBox,
		initPurchaseSlider,
		initPurchaseExplorer,
		initStaggerUpAnimation,
		initFooter,
	].forEach(safeRun);

	// Execute page-specific functions
	const isTemplatePage = (templateName) =>
		root.classList.contains(templateName);

	if (isTemplatePage("template-collection")) {
		safeRun(initPLP);
	}
	if (isTemplatePage("template-product")) {
		[initPDP, initRelatedProduct].forEach(safeRun);
	}
	if (isTemplatePage("template-page-about")) {
		[initEnvelopeAnimation, initChapterAnimation].forEach(safeRun);
	}
	safeRun(initWholesalePage);
	if (isTemplatePage("template-page-shipping-and-returns")) {
		safeRun(initShippingAndReturnsPage);
	}
	if (isTemplatePage("template-blog-index")) {
		safeRun(initBlog);
	}
	if (isTemplatePage("template-404")) {
		safeRun(init404);
	}
	// Add more page-specific initializations as needed

	// Initialize aria focus manager LAST, after all other components have set their aria-hidden states
	setTimeout(() => {
		ariaFocusManager.init();
	}, 0);
});
